import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  Download, Shuffle, Calendar, TrendingUp, Sparkles, ShieldCheck, Target,
  BarChart3, Quote, Palette, ChevronDown, RefreshCw, Users, Megaphone,
  MessageSquare, Rocket, Star, Clock, Award, ArrowRight, CheckCircle2,
} from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import AdminTopNav from "../components/AdminTopNav";
import { isAdmin, loginUrl, getAuthHeaders } from "../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Trading-week helpers ────────────────────────────────────────────────
// Compute Monday + Friday of a given ISO week number / year (Thursday rule).
function tradingWeekRange(isoWeek, isoYear) {
  // ISO-8601: Week 1 is the week containing Jan 4. Find Thursday of that week,
  // then walk back to Monday and forward to Friday.
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7; // Mon=1..Sun=7
  const monWeek1 = new Date(jan4);
  monWeek1.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
  const monday = new Date(monWeek1);
  monday.setUTCDate(monWeek1.getUTCDate() + (isoWeek - 1) * 7);
  const friday = new Date(monday);
  friday.setUTCDate(monday.getUTCDate() + 4);
  return { monday, friday };
}

// Return the LAST COMPLETED Mo–Fr trading week. If today is Mo–Fr → last week.
// If today is Sat/Sun → this week (Mo–Fr just closed).
function latestCompletedTradingWeek(today = new Date()) {
  const dow = today.getDay(); // 0=Sun..6=Sat
  const daysSinceMonday = (dow + 6) % 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysSinceMonday);
  if (dow >= 1 && dow <= 5) {
    monday.setDate(monday.getDate() - 7);
  }
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  // ISO week of the Thursday inside the range
  const isoTarget = new Date(monday);
  isoTarget.setDate(monday.getDate() + 3);
  // Strict ISO algorithm: week = floor((thursday - jan4_of_thursday_year) / 7) + 1
  const thursdayYear = isoTarget.getFullYear();
  const jan4 = new Date(thursdayYear, 0, 4);
  const jan4Dow = jan4.getDay() || 7;
  const monWeek1 = new Date(jan4);
  monWeek1.setDate(jan4.getDate() - (jan4Dow - 1));
  const week = Math.floor((isoTarget - monWeek1) / (7 * 86400000)) + 1;
  return { week, year: thursdayYear, monday, friday };
}

// Render a date range as "KW XX · DD.MM.–DD.MM.YYYY".
function formatTradingWeekString(week, year, monday, friday) {
  const fmt = (d) => d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" }).replace(/\.$/, "");
  return `KW ${week} · ${fmt(monday)}.–${fmt(friday)}.${friday.getFullYear()}`;
}

// ── Mode (Täglich / Wöchentlich / Monatlich) ───────────────────────────
// Same generator powers all three reports — only labels + default date range
// switch. URL param ?mode=monatlich/woechentlich/taeglich selects the mode.
// Saturday reminder mail deep-links to ?mode=woechentlich&kw=XX.
const MODES = {
  taeglich: {
    badge: "TAGESBERICHT",
    title: "TAGESPERFORMANCE",
    titleMixed: "Tagesperformance",
    metric: "TAGESRENDITE",
    metricMixed: "Rendite gestern",
    filenamePrefix: "tagesbericht",
    sparklineHint: "99, 100, 100.5, 102 (z. B. Intraday-Stufen)",
    defaultSparkline: [99.5, 100, 100.4, 100.8, 101.2, 102.1],
  },
  woechentlich: {
    badge: "WOCHENBERICHT",
    title: "WOCHENPERFORMANCE",
    titleMixed: "Wochenperformance",
    metric: "WOCHENRENDITE",
    metricMixed: "Rendite diese Woche",
    filenamePrefix: "wochenbericht",
    sparklineHint: "98, 100, 99, 102, 101, 104, 107 (z. B. 7 Tage)",
    defaultSparkline: [98, 100, 99, 102, 101, 104, 107],
  },
  monatlich: {
    badge: "MONATSBERICHT",
    title: "MONATSPERFORMANCE",
    titleMixed: "Monatsperformance",
    metric: "MONATSRENDITE",
    metricMixed: "Rendite diesen Monat",
    filenamePrefix: "monatsbericht",
    sparklineHint: "100, 101.5, 103, 105.2 (z. B. 4 Wochen)",
    defaultSparkline: [100, 101.5, 103, 105.2],
  },
};

// ── Categories (Performance / Webinar / Quote / Promo / Testimonial) ───
const CATEGORIES = {
  performance: { label: "Performance", icon: TrendingUp, blurb: "Wochen-/Monats-/Tagesrendite mit Chart" },
  webinar:     { label: "Webinar",     icon: Users,      blurb: "Nächstes Live-Webinar mit Datum + Speaker" },
  quote:       { label: "Zitat",       icon: Quote,      blurb: "Großes Zitat-Bild für Engagement-Posts" },
  promo:       { label: "Promo",       icon: Megaphone,  blurb: "Generischer CTA — Insider-Report, Onboarding, etc." },
  testimonial: { label: "Testimonial", icon: MessageSquare, blurb: "Kundenstimme mit Sternen + Name" },
};

// ── Design Variants ─────────────────────────────────────────────────────
// 3 layouts × 4 color palettes × 3 background patterns = 36 unique looks.
// All variants share the Alphalane logo + footer wordmark so the brand
// stays recognizable regardless of which combination is picked.

const PALETTES = [
  { id: "blue-violet",  from: "#3DBEFF", to: "#8B5CF6", glow: "#5B7BF7" },
  { id: "violet-pink",  from: "#8B5CF6", to: "#EC4899", glow: "#A855F7" },
  { id: "cyan-blue",    from: "#06B6D4", to: "#3B82F6", glow: "#0EA5E9" },
  { id: "indigo-magenta", from: "#6366F1", to: "#D946EF", glow: "#7C3AED" },
  { id: "emerald-cyan", from: "#34D399", to: "#0EA5E9", glow: "#10B981" },
  { id: "sunset",       from: "#F59E0B", to: "#EC4899", glow: "#F97316" },
];

const PATTERNS = ["dots", "glow", "mesh", "grid", "aurora"];
const LAYOUTS = ["classic", "split", "quote", "light"];

const QUOTES = [
  "Logik statt Emotion.",
  "Disziplin schlägt Bauchgefühl.",
  "Datenbasiert. Emotionsfrei. Konsequent.",
  "Märkte wechseln. Strategie bleibt.",
  "Geduld ist die teuerste Währung.",
];

// Deterministic "market noise" — densifies a sparse series with interpolated
// points + seeded jitter so performance lines get realistic highs & lows.
// Endpoints of each segment stay exact, so KPI values remain truthful.
function densifySeries(series, sub = 5, vol = 0.13) {
  if (!series || series.length < 2) return series || [];
  const range = Math.max(...series) - Math.min(...series) || 1;
  let seed = series.reduce((s, v, i) => s + v * (i + 3.7), 11.31);
  const rnd = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280 - 0.5;
  };
  const out = [];
  for (let i = 0; i < series.length - 1; i++) {
    const a = series[i];
    const b = series[i + 1];
    out.push(a);
    for (let k = 1; k < sub; k++) {
      const t = k / sub;
      const base = a + (b - a) * t;
      const damp = Math.sin(Math.PI * t); // zero jitter at segment endpoints
      out.push(base + rnd() * range * vol * damp);
    }
  }
  out.push(series[series.length - 1]);
  return out;
}

// Default sparkline kept for backward-compat references inside this file.
const DEFAULT_SPARKLINE = MODES.woechentlich.defaultSparkline;

// ── Promo / Quote / Testimonial / Webinar presets ──────────────────────
const PROMO_PRESETS = [
  {
    id: "insider",
    headline: "DER INSIDER-REPORT",
    sub: "Wie eine KI seit 9 Monaten profitabel handelt — emotionslos.",
    bullets: ["100% automatisiert", "Tagesaktueller Track Record", "Jederzeit kündbar"],
    cta: "JETZT GRATIS SICHERN",
    url: "alphalane-tradingsystems.com",
  },
  {
    id: "launch",
    headline: "JETZT VERFÜGBAR",
    sub: "Hedge-Fund-Strategien — jetzt erstmals für Privatanleger.",
    bullets: ["Ab 5.000 CHF Startkapital", "Reguliertes Broker-Konto", "Du behältst die Kontrolle"],
    cta: "JETZT ANFRAGEN",
    url: "alphalane-tradingsystems.com",
  },
  {
    id: "track",
    headline: "LIVE TRACK RECORD",
    sub: "Tagesaktuelle Performance — transparent & überprüfbar.",
    bullets: ["Veröffentlicht jede Woche", "Verifiziert via MT5", "Keine Cherry-Picks"],
    cta: "TRACK RECORD ANSEHEN",
    url: "alphalane-tradingsystems.com",
  },
  {
    id: "psych",
    headline: "90% VERLIEREN",
    sub: "…weil sie selbst handeln. Du musst nicht zu den 10% gehören.",
    bullets: ["Disziplin statt Bauchgefühl", "Logik schlägt Emotion", "Risiko-Management eingebaut"],
    cta: "MEHR ERFAHREN",
    url: "alphalane-tradingsystems.com",
  },
];

const TESTIMONIALS = [
  { name: "Marc S.", role: "Kunde seit 6 Monaten", text: "Endlich ein System, das tatsächlich liefert was es verspricht. Ich muss mich um nichts kümmern.", rating: 5 },
  { name: "Daniela B.", role: "Kundin seit 4 Monaten", text: "Transparent, professionell und nachvollziehbar. Genau das was ich gesucht habe.", rating: 5 },
  { name: "Pascal W.", role: "Kunde seit 9 Monaten", text: "Die Performance spricht für sich. Mein bestes Investment 2026.", rating: 5 },
];

// ── Component ───────────────────────────────────────────────────────────
export default function AdminWeeklyReport() {
  const navigate = useNavigate();
  const captureRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  // iOS/Mobile: Fallback-Vorschau ("lange drücken → Bild sichern"), weil
  // Safari auf dem iPhone programmatische Downloads (<a download>) ignoriert.
  const [mobilePreview, setMobilePreview] = useState(null);

  // Category (performance / webinar / quote / promo / testimonial)
  const [category, setCategory] = useState(() => {
    const c = new URLSearchParams(window.location.search).get("category");
    if (c && CATEGORIES[c]) return c;
    return "performance";
  });

  // Mode (taeglich | woechentlich | monatlich) — performance-only.
  const [mode, setMode] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("mode");
    if (p === "monatlich" || p === "woechentlich" || p === "taeglich") return p;
    // /admin/monatsbericht route → default to monthly mode
    if (window.location.pathname.startsWith("/admin/monatsbericht")) return "monatlich";
    return "woechentlich";
  });
  const modeCfg = MODES[mode];

  // Performance state
  const [returnPct, setReturnPct] = useState("3.42");
  const [strategy, setStrategy] = useState("ALPHA AI");
  const [dateRange, setDateRange] = useState("");
  const [sparklineStr, setSparklineStr] = useState(MODES.woechentlich.defaultSparkline.join(", "));
  const [quote, setQuote] = useState(QUOTES[0]);

  // Webinar state
  const [webinarList, setWebinarList] = useState([]);
  const [webinarLoading, setWebinarLoading] = useState(false);
  const [webinarId, setWebinarId] = useState("");
  const [webinarTitle, setWebinarTitle] = useState("Live-Webinar");
  const [webinarSubtitle, setWebinarSubtitle] = useState("Erlebe das System live – gemeinsam mit Pascal & Adrian.");
  const [webinarDate, setWebinarDate] = useState("");
  const [webinarTime, setWebinarTime] = useState("20:00 Uhr Schweizer Zeit");
  const [webinarCta, setWebinarCta] = useState("JETZT ANMELDEN");
  const [webinarBullet1, setWebinarBullet1] = useState("System live in Aktion");
  const [webinarBullet2, setWebinarBullet2] = useState("Live-Q&A mit Pascal & Adrian");
  const [webinarBullet3, setWebinarBullet3] = useState("Echte Performance-Daten");

  // Webinar stats + chart (Option C: auto-fetch from /track-record with
  // per-field manual overrides). Chart values are a comma-separated series
  // of cumulative % returns for the last N months. Empty chartStr disables
  // the chart section so the layout gracefully degrades.
  const [webinarStat1Val, setWebinarStat1Val] = useState("+24,9%");
  const [webinarStat1Lbl, setWebinarStat1Lbl] = useState("Monatsreturn");
  const [webinarStat2Val, setWebinarStat2Val] = useState("+383%");
  const [webinarStat2Lbl, setWebinarStat2Lbl] = useState("Gesamtreturn seit Mai 2025");
  const [webinarChartStr, setWebinarChartStr] = useState("47, 111, 109, 147, 202, 248, 282, 401, 287, 383");
  const [webinarChartLabel, setWebinarChartLabel] = useState("Track Record — kumulierte Rendite in %");
  // Webinar layout: "duo", "performance", "minimal", "spotlight", "ticket",
  // "slogan"/"stat"/"terminal" (ohne Portraits).
  const [webinarLayout, setWebinarLayout] = useState("duo");
  const [webinarSlogan, setWebinarSlogan] = useState(QUOTES[0]);

  // Promo state
  const [promoPresetIdx, setPromoPresetIdx] = useState(0);
  const [promoHeadline, setPromoHeadline] = useState(PROMO_PRESETS[0].headline);
  const [promoSub, setPromoSub] = useState(PROMO_PRESETS[0].sub);
  const [promoB1, setPromoB1] = useState(PROMO_PRESETS[0].bullets[0]);
  const [promoB2, setPromoB2] = useState(PROMO_PRESETS[0].bullets[1]);
  const [promoB3, setPromoB3] = useState(PROMO_PRESETS[0].bullets[2]);
  const [promoCta, setPromoCta] = useState(PROMO_PRESETS[0].cta);
  const [promoUrl, setPromoUrl] = useState(PROMO_PRESETS[0].url);

  // Testimonial state
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [testimonialText, setTestimonialText] = useState(TESTIMONIALS[0].text);
  const [testimonialName, setTestimonialName] = useState(TESTIMONIALS[0].name);
  const [testimonialRole, setTestimonialRole] = useState(TESTIMONIALS[0].role);
  const [testimonialRating, setTestimonialRating] = useState(TESTIMONIALS[0].rating);

  // Big-quote state (separate from sparkline quote layout)
  const [bigQuoteText, setBigQuoteText] = useState("Logik statt Emotion.");
  const [bigQuoteAuthor, setBigQuoteAuthor] = useState("Alphalane AI");

  // Auto-fill date range whenever mode changes (only matters in performance).
  useEffect(() => {
    if (mode === "monatlich") {
      const now = new Date();
      const monthName = now.toLocaleDateString("de-CH", { month: "long", year: "numeric" });
      setDateRange(monthName.toUpperCase());
      setSparklineStr(MODES.monatlich.defaultSparkline.join(", "));
    } else if (mode === "taeglich") {
      // Default: yesterday (German DD.MM.YYYY).
      const d = new Date();
      d.setDate(d.getDate() - 1);
      setDateRange(d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }));
      setSparklineStr(MODES.taeglich.defaultSparkline.join(", "));
    } else {
      const { week, year, monday, friday } = latestCompletedTradingWeek();
      setDateRange(formatTradingWeekString(week, year, monday, friday));
      setSparklineStr(MODES.woechentlich.defaultSparkline.join(", "));
    }
  }, [mode]);

  // Fetch upcoming webinars when category=webinar.
  useEffect(() => {
    if (category !== "webinar") return;
    let abort = false;
    (async () => {
      setWebinarLoading(true);
      try {
        const r = await axios.get(`${API}/admin/webinars`, { headers: getAuthHeaders() });
        if (abort) return;
        const all = Array.isArray(r.data) ? r.data : [];
        const now = Date.now();
        // Upcoming, non-archived, sorted ascending by scheduled_at.
        const upcoming = all
          .filter((w) => !w.is_archived && w.scheduled_at && new Date(w.scheduled_at).getTime() > now - 6 * 3600 * 1000)
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        setWebinarList(upcoming);
        if (upcoming.length && !webinarId) {
          applyWebinar(upcoming[0]);
        }
      } catch (e) {
        console.warn("Webinar fetch failed", e);
      } finally {
        if (!abort) setWebinarLoading(false);
      }

      // Auto-fetch track record for chart + stat auto-population. Purely
      // additive — if the request fails or the DB is empty the manually-set
      // defaults stay in place, so the template never renders "empty".
      try {
        const tr = await axios.get(`${API}/track-record`);
        if (abort) return;
        const rows = Array.isArray(tr.data) ? tr.data : [];
        if (rows.length) {
          const active = rows.filter((r) => !r.paused);
          const cum = rows[rows.length - 1]?.cumulative ?? 0;
          const latest = active[active.length - 1];
          const latestMonthly = Number(latest?.monthly) || 0;
          // Extract month name (strip year), e.g. "April 2026" → "April"
          const latestMonthName = (latest?.month || "").split(" ")[0];
          const chart = rows.map((r) => Number(r.cumulative) || 0).slice(-10).join(", ");
          const fmtSign = (n) => `${n > 0 ? "+" : ""}${n.toFixed(n < 100 && n > -100 ? 1 : 0).replace(".", ",")}%`;
          setWebinarStat1Val(fmtSign(latestMonthly));
          if (latestMonthName) setWebinarStat1Lbl(`Monatsreturn ${latestMonthName}`);
          setWebinarStat2Val(fmtSign(cum));
          setWebinarStat2Lbl("Gesamtreturn seit Mai 2025");
          setWebinarChartStr(chart);
        }
      } catch (e) {
        console.warn("Track-record fetch failed (using defaults)", e);
      }
    })();
    return () => { abort = true; };
  }, [category]);

  const applyWebinar = (w) => {
    if (!w) return;
    setWebinarId(w.id);
    setWebinarTitle("Live-Webinar");
    setWebinarSubtitle(w.pitch_one_liner || w.description || "Erlebe das System live – gemeinsam mit Pascal & Adrian.");
    try {
      const d = new Date(w.scheduled_at);
      const dateStr = d.toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
      const timeStr = d.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
      setWebinarDate(dateStr);
      setWebinarTime(`${timeStr} Uhr Schweizer Zeit`);
    } catch {
      setWebinarDate("");
    }
    if (w.bullets && w.bullets.length) {
      setWebinarBullet1(w.bullets[0] || "System live in Aktion");
      setWebinarBullet2(w.bullets[1] || "Live-Q&A mit Pascal & Adrian");
      setWebinarBullet3(w.bullets[2] || "Echte Performance-Daten");
    }
  };

  const applyPromoPreset = (idx) => {
    setPromoPresetIdx(idx);
    const p = PROMO_PRESETS[idx];
    setPromoHeadline(p.headline);
    setPromoSub(p.sub);
    setPromoB1(p.bullets[0]);
    setPromoB2(p.bullets[1]);
    setPromoB3(p.bullets[2]);
    setPromoCta(p.cta);
    setPromoUrl(p.url);
  };

  const applyTestimonialPreset = (idx) => {
    setTestimonialIdx(idx);
    const t = TESTIMONIALS[idx];
    setTestimonialText(t.text);
    setTestimonialName(t.name);
    setTestimonialRole(t.role);
    setTestimonialRating(t.rating);
  };

  // 1-click "Letzte Handelswoche"-Reset.
  const resetToLatestTradingWeek = () => {
    const { week, year, monday, friday } = latestCompletedTradingWeek();
    setDateRange(formatTradingWeekString(week, year, monday, friday));
    toast.success(`KW ${week} eingesetzt (${monday.toLocaleDateString("de-CH")} – ${friday.toLocaleDateString("de-CH")})`);
  };

  // Auto-expand a pure number input ("25") → full "KW 25 · 15.06.–19.06.2026".
  const onDateRangeChange = (raw) => {
    setDateRange(raw);
    const justNumber = raw.trim().match(/^(\d{1,2})$/);
    if (justNumber && mode === "woechentlich") {
      const kw = parseInt(justNumber[1], 10);
      if (kw >= 1 && kw <= 53) {
        const year = new Date().getFullYear();
        const { monday, friday } = tradingWeekRange(kw, year);
        setDateRange(formatTradingWeekString(kw, year, monday, friday));
      }
    }
  };

  // Design state
  const [layout, setLayout] = useState("classic");
  const [paletteIdx, setPaletteIdx] = useState(0);
  const [pattern, setPattern] = useState("dots");

  useEffect(() => {
    if (!isAdmin()) navigate(loginUrl());
    document.title = "Social-Media-Generator · Alphalane";
  }, [navigate]);

  const palette = PALETTES[paletteIdx];

  // Parse sparkline string into numbers
  const sparkline = useMemo(() => {
    const nums = sparklineStr.split(/[\s,]+/).map((s) => parseFloat(s.trim())).filter((n) => !Number.isNaN(n));
    return nums.length >= 2 ? nums : DEFAULT_SPARKLINE;
  }, [sparklineStr]);

  const randomize = () => {
    const newLayout = LAYOUTS[Math.floor(Math.random() * LAYOUTS.length)];
    const newPalette = Math.floor(Math.random() * PALETTES.length);
    const newPattern = PATTERNS[Math.floor(Math.random() * PATTERNS.length)];
    const newQuote = QUOTES[Math.floor(Math.random() * QUOTES.length)];
    if (category === "performance") setLayout(newLayout);
    setPaletteIdx(newPalette);
    setPattern(newPattern);
    if (category === "performance") setQuote(newQuote);
    if (category === "quote") {
      setBigQuoteText(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
    }
    if (category === "promo") {
      const idx = Math.floor(Math.random() * PROMO_PRESETS.length);
      applyPromoPreset(idx);
    }
    if (category === "testimonial") {
      const idx = Math.floor(Math.random() * TESTIMONIALS.length);
      applyTestimonialPreset(idx);
    }
    toast.success("Neues Design gewürfelt 🎲");
  };

  const downloadPng = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      // Wait for fonts + logo to fully load — otherwise html-to-image
      // captures placeholder boxes / unstyled text on slow renders.
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      const logoImg = captureRef.current.querySelector("img");
      if (logoImg && !logoImg.complete) {
        await new Promise((res) => {
          logoImg.addEventListener("load", res, { once: true });
          logoImg.addEventListener("error", res, { once: true });
        });
      }
      // Render at exact 1080×1920 (Story format). pixelRatio:2 →
      // final PNG is 2160×3840 (~6 MB), razor-sharp on every phone
      // display AND well under WhatsApp's 8 MB Status size limit.
      const pngOptions = {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: (category === "performance" && layout === "light") ? "#F0EAFB" : "#050816",
        width: 1080,
        height: 1920,
        canvasWidth: 1080,
        canvasHeight: 1920,
        style: {
          transform: "scale(1)",
          transformOrigin: "top left",
          width: "1080px",
          height: "1920px",
        },
      };
      const ua = navigator.userAgent || "";
      const isIOS = /iP(hone|od|ad)/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
      // Safari/iOS-Quirk: Bilder (Portraits/Logo) fehlen oft im ersten
      // Render — deshalb 2 Warm-up-Durchläufe vor dem finalen Capture.
      if (isIOS || isSafari) {
        await toPng(captureRef.current, pngOptions);
        await toPng(captureRef.current, pngOptions);
      }
      const dataUrl = await toPng(captureRef.current, pngOptions);
      const safeName = (returnPct || "0").toString().replace(/[^0-9.,-]/g, "");
      const cat = category === "performance" ? modeCfg.filenamePrefix : category;
      const filename = category === "performance"
        ? `alphalane-${modeCfg.filenamePrefix}-${safeName}prozent.png`
        : `alphalane-${cat}-${Date.now()}.png`;

      if (isIOS) {
        // 1) Web Share API → Share-Sheet mit "Bild sichern" (Fotos), WhatsApp, Instagram…
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], filename, { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file] });
            toast.success("Im Share-Sheet 'Bild sichern' wählen 📸");
            return;
          }
        } catch (err) {
          if (err && err.name === "AbortError") return; // Nutzer hat abgebrochen
          // NotAllowedError etc. → Fallback-Vorschau unten
        }
        // 2) Fallback: Vorschau-Overlay — lange drücken → "Bild sichern".
        setMobilePreview({ dataUrl, filename });
        return;
      }

      const a = document.createElement("a");
      a.download = filename;
      a.href = dataUrl;
      a.click();
      toast.success("PNG heruntergeladen 🎉");
    } catch (e) {
      toast.error(`Export fehlgeschlagen: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const numericReturn = parseFloat(returnPct.replace(",", "."));
  const isNegative = numericReturn < 0;
  const displayPct = `${isNegative ? "" : "+"}${returnPct.replace(".", ",")}%`;

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="admin-weekly-report">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin · Social-Media</span>
            </div>
          </Link>
        </div>
      </header>

      <AdminTopNav />

      {/* iOS-Fallback: Bild-Vorschau zum "lange drücken → Bild sichern" */}
      {mobilePreview && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex flex-col items-center justify-center p-5"
          data-testid="mobile-save-overlay"
          onClick={() => setMobilePreview(null)}
        >
          <div className="text-white text-sm font-display font-semibold mb-3 text-center px-4">
            Bild <strong>lange gedrückt halten</strong> und „Bild sichern" wählen 📸
          </div>
          <img
            src={mobilePreview.dataUrl}
            alt="Social Media Grafik"
            data-testid="mobile-save-image"
            className="max-h-[70vh] max-w-full rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setMobilePreview(null)}
            data-testid="mobile-save-close"
            className="mt-4 px-6 py-2.5 rounded-full bg-white/15 text-white text-sm font-display font-semibold border border-white/25"
          >
            Schließen
          </button>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Social-Media-Generator</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Erstelle Social-Media-Stories im Alphalane-Branding — 1080×1920 PNG (9:16 Hochkant) für WhatsApp-Status und Instagram-Story.
            </p>
            {/* Category toggle */}
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="generator-category-toggle">
              {Object.entries(CATEGORIES).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const active = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCategory(key)}
                    data-testid={`generator-category-${key}`}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-display font-semibold transition-all border ${
                      active
                        ? "al-grad-bg text-white shadow-sm border-transparent"
                        : "bg-white text-slate-600 hover:text-slate-900 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {cfg.label}
                  </button>
                );
              })}
            </div>
            {/* Mode toggle (performance only) */}
            {category === "performance" && (
              <div className="mt-3 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="weekly-report-mode-toggle">
                {Object.entries(MODES).map(([key]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setMode(key)}
                    data-testid={`weekly-report-mode-${key}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-display font-semibold transition-all ${
                      mode === key ? "al-grad-bg text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {key === "woechentlich" ? "Wöchentlich" : key === "monatlich" ? "Monatlich" : "Täglich"}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={randomize}
              data-testid="weekly-report-randomize"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 text-sm font-display font-semibold transition-colors"
            >
              <Shuffle className="h-4 w-4" /> Design würfeln
            </button>
            <button
              type="button"
              onClick={downloadPng}
              disabled={exporting}
              data-testid="weekly-report-download"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full al-grad-bg text-white text-sm font-display font-bold hover:opacity-90 disabled:opacity-60 transition-opacity shadow-md shadow-violet-200"
            >
              <Download className="h-4 w-4" /> {exporting ? "Wird exportiert…" : "PNG herunterladen"}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[400px_1fr] gap-6">
          {/* ── Form Panel ─────────────────────────────────────────── */}
          <section className="al-card p-5 space-y-4 self-start" data-testid="weekly-report-form">
            {category === "performance" && (<>
            <div>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-1.5">
                  <TrendingUp className="h-3.5 w-3.5" /> {mode === "monatlich" ? "Monatsrendite (%)" : mode === "taeglich" ? "Tagesrendite (%)" : "Wochenrendite (%)"}
                </span>
                <input
                  type="text"
                  value={returnPct}
                  onChange={(e) => setReturnPct(e.target.value)}
                  data-testid="weekly-report-return"
                  placeholder="z.B. 3.42 oder -1.20"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-base font-mono-tab font-bold focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
                />
                <p className="mt-1 text-[11px] text-slate-500">Negative Werte → roter Tint, +/- automatisch.</p>
              </label>
            </div>

            <div>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-1.5">
                  <Calendar className="h-3.5 w-3.5" /> {mode === "monatlich" ? "Monat / Jahr" : mode === "taeglich" ? "Tag (Datum)" : "Zeitraum / KW"}
                </span>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={dateRange}
                    onChange={(e) => onDateRangeChange(e.target.value)}
                    data-testid="weekly-report-daterange"
                    className="flex-1 min-w-0 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-mono-tab focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
                  />
                  {mode === "woechentlich" && (
                    <button
                      type="button"
                      onClick={resetToLatestTradingWeek}
                      data-testid="weekly-report-daterange-reset"
                      title="Letzte abgeschlossene Handelswoche einsetzen"
                      className="shrink-0 px-3 py-2.5 rounded-xl border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {mode === "woechentlich" && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Tipp: einfach nur die KW-Nummer eintippen (z. B. <code className="font-mono-tab">26</code>) — das Datum wird automatisch eingefügt. Oder klick auf <RefreshCw className="inline h-3 w-3" /> für die letzte abgeschlossene Handelswoche.
                  </p>
                )}
              </label>
            </div>

            <div>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> Strategie-Name
                </span>
                <input
                  type="text"
                  value={strategy}
                  onChange={(e) => setStrategy(e.target.value)}
                  data-testid="weekly-report-strategy"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
                />
              </label>
            </div>

            <div>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-1.5">
                  <BarChart3 className="h-3.5 w-3.5" /> Chart-Punkte (Komma-getrennt, mind. 2)
                </span>
                <input
                  type="text"
                  value={sparklineStr}
                  onChange={(e) => setSparklineStr(e.target.value)}
                  data-testid="weekly-report-sparkline"
                  placeholder={modeCfg.sparklineHint}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-mono-tab focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
                />
                <p className="mt-1 text-[11px] text-slate-500">{sparkline.length} Punkte erkannt. Absolute Werte oder Prozente — egal, wird normiert.</p>
              </label>
            </div>

            {layout === "quote" && (
              <div>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-1.5">
                    <Quote className="h-3.5 w-3.5" /> Spruch (nur bei Quote-Layout)
                  </span>
                  <input
                    type="text"
                    value={quote}
                    onChange={(e) => setQuote(e.target.value)}
                    data-testid="weekly-report-quote"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
                  />
                </label>
              </div>
            )}
            </>)}

            {category === "webinar" && (
              <WebinarForm
                webinarList={webinarList}
                webinarLoading={webinarLoading}
                webinarId={webinarId}
                setWebinarId={setWebinarId}
                applyWebinar={applyWebinar}
                webinarTitle={webinarTitle}
                setWebinarTitle={setWebinarTitle}
                webinarSubtitle={webinarSubtitle}
                setWebinarSubtitle={setWebinarSubtitle}
                webinarDate={webinarDate}
                setWebinarDate={setWebinarDate}
                webinarTime={webinarTime}
                setWebinarTime={setWebinarTime}
                webinarCta={webinarCta}
                setWebinarCta={setWebinarCta}
                webinarBullet1={webinarBullet1}
                setWebinarBullet1={setWebinarBullet1}
                webinarBullet2={webinarBullet2}
                setWebinarBullet2={setWebinarBullet2}
                webinarBullet3={webinarBullet3}
                setWebinarBullet3={setWebinarBullet3}
                webinarStat1Val={webinarStat1Val}
                setWebinarStat1Val={setWebinarStat1Val}
                webinarStat1Lbl={webinarStat1Lbl}
                setWebinarStat1Lbl={setWebinarStat1Lbl}
                webinarStat2Val={webinarStat2Val}
                setWebinarStat2Val={setWebinarStat2Val}
                webinarStat2Lbl={webinarStat2Lbl}
                setWebinarStat2Lbl={setWebinarStat2Lbl}
                webinarChartStr={webinarChartStr}
                setWebinarChartStr={setWebinarChartStr}
                webinarChartLabel={webinarChartLabel}
                setWebinarChartLabel={setWebinarChartLabel}
                webinarSlogan={webinarSlogan}
                setWebinarSlogan={setWebinarSlogan}
              />
            )}

            {category === "quote" && (
              <QuoteForm
                bigQuoteText={bigQuoteText}
                setBigQuoteText={setBigQuoteText}
                bigQuoteAuthor={bigQuoteAuthor}
                setBigQuoteAuthor={setBigQuoteAuthor}
              />
            )}

            {category === "promo" && (
              <PromoForm
                promoPresetIdx={promoPresetIdx}
                applyPromoPreset={applyPromoPreset}
                promoHeadline={promoHeadline}
                setPromoHeadline={setPromoHeadline}
                promoSub={promoSub}
                setPromoSub={setPromoSub}
                promoB1={promoB1}
                setPromoB1={setPromoB1}
                promoB2={promoB2}
                setPromoB2={setPromoB2}
                promoB3={promoB3}
                setPromoB3={setPromoB3}
                promoCta={promoCta}
                setPromoCta={setPromoCta}
                promoUrl={promoUrl}
                setPromoUrl={setPromoUrl}
              />
            )}

            {category === "testimonial" && (
              <TestimonialForm
                testimonialIdx={testimonialIdx}
                applyTestimonialPreset={applyTestimonialPreset}
                testimonialText={testimonialText}
                setTestimonialText={setTestimonialText}
                testimonialName={testimonialName}
                setTestimonialName={setTestimonialName}
                testimonialRole={testimonialRole}
                setTestimonialRole={setTestimonialRole}
                testimonialRating={testimonialRating}
                setTestimonialRating={setTestimonialRating}
              />
            )}

            <div className="pt-2 border-t border-slate-100 space-y-3">
              {category === "performance" && (
                <div>
                  <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                    <Palette className="h-3.5 w-3.5" /> Layout
                  </span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {LAYOUTS.map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setLayout(l)}
                        data-testid={`weekly-report-layout-${l}`}
                        className={`px-2 py-2 rounded-lg text-xs font-display font-semibold border transition-colors ${
                          layout === l ? "border-violet-400 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        {l === "classic" ? "Klassisch" : l === "split" ? "Minimal" : l === "quote" ? "Quote" : "Hell"}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {category === "webinar" && (
                <div>
                  <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-2">
                    <Palette className="h-3.5 w-3.5" /> Webinar-Layout
                  </span>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { id: "duo", label: "Duo-Portrait" },
                      { id: "performance", label: "Performance" },
                      { id: "minimal", label: "Minimal" },
                      { id: "spotlight", label: "Spotlight" },
                      { id: "ticket", label: "Ticket" },
                      { id: "slogan", label: "Slogan" },
                      { id: "stat", label: "Big Stat" },
                      { id: "terminal", label: "Terminal" },
                    ].map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setWebinarLayout(l.id)}
                        data-testid={`webinar-layout-${l.id}`}
                        className={`px-2 py-2 rounded-lg text-[11px] font-display font-semibold border transition-colors ${
                          webinarLayout === l.id ? "border-violet-400 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 block">Farbverlauf</span>
                <div className="flex gap-2 flex-wrap">
                  {PALETTES.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPaletteIdx(i)}
                      data-testid={`weekly-report-palette-${p.id}`}
                      className={`h-9 w-9 rounded-full transition-all ${paletteIdx === i ? "ring-2 ring-offset-2 ring-violet-400 scale-110" : "hover:scale-105"}`}
                      style={{ background: `linear-gradient(135deg, ${p.from}, ${p.to})` }}
                      title={p.id}
                    />
                  ))}
                </div>
              </div>

              <div>
                <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 block">Hintergrund</span>
                <div className="grid grid-cols-3 gap-1.5">
                  {PATTERNS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPattern(p)}
                      data-testid={`weekly-report-pattern-${p}`}
                      className={`px-2 py-2 rounded-lg text-xs font-display font-semibold border transition-colors ${
                        pattern === p ? "border-violet-400 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {p === "dots" ? "Punkte" : p === "glow" ? "Glow" : p === "mesh" ? "Mesh" : p === "grid" ? "Raster" : "Aurora"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── Preview Panel ─────────────────────────────────────── */}
          <section className="space-y-3" data-testid="weekly-report-preview">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
              Vorschau · 1080×1920 (Story / 9:16 Hochkant)
            </div>
            <div className="al-card overflow-hidden bg-slate-100 p-4">
              <div className="max-w-[400px] mx-auto">
                <div className="relative" style={{ aspectRatio: "9/16" }}>
                  <div
                    ref={captureRef}
                    className="absolute inset-0"
                    style={{ width: 1080, height: 1920, transform: "scale(0.37)", transformOrigin: "top left" }}
                  >
                    <ReportCanvas
                      category={category}
                      layout={layout}
                      palette={palette}
                      pattern={pattern}
                      displayPct={displayPct}
                      isNegative={isNegative}
                      strategy={strategy}
                      dateRange={dateRange}
                      sparkline={sparkline}
                      quote={quote}
                      modeCfg={modeCfg}
                      returnPct={returnPct}
                      // webinar
                      webinarLayout={webinarLayout}
                      webinarSlogan={webinarSlogan}
                      webinarTitle={webinarTitle}
                      webinarSubtitle={webinarSubtitle}
                      webinarDate={webinarDate}
                      webinarTime={webinarTime}
                      webinarCta={webinarCta}
                      webinarBullet1={webinarBullet1}
                      webinarBullet2={webinarBullet2}
                      webinarBullet3={webinarBullet3}
                      webinarStat1Val={webinarStat1Val}
                      webinarStat1Lbl={webinarStat1Lbl}
                      webinarStat2Val={webinarStat2Val}
                      webinarStat2Lbl={webinarStat2Lbl}
                      webinarChartStr={webinarChartStr}
                      webinarChartLabel={webinarChartLabel}
                      // quote
                      bigQuoteText={bigQuoteText}
                      bigQuoteAuthor={bigQuoteAuthor}
                      // promo
                      promoHeadline={promoHeadline}
                      promoSub={promoSub}
                      promoB1={promoB1}
                      promoB2={promoB2}
                      promoB3={promoB3}
                      promoCta={promoCta}
                      promoUrl={promoUrl}
                      // testimonial
                      testimonialText={testimonialText}
                      testimonialName={testimonialName}
                      testimonialRole={testimonialRole}
                      testimonialRating={testimonialRating}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

// ── Report Canvas (the actual exported PNG content) ────────────────────
function ReportCanvas({
  category, layout, palette, pattern, displayPct, isNegative, strategy, dateRange,
  sparkline, quote, modeCfg, returnPct,
  webinarLayout,
  webinarSlogan,
  webinarTitle, webinarSubtitle, webinarDate, webinarTime, webinarCta,
  webinarBullet1, webinarBullet2, webinarBullet3,
  webinarStat1Val, webinarStat1Lbl, webinarStat2Val, webinarStat2Lbl,
  webinarChartStr, webinarChartLabel,
  bigQuoteText, bigQuoteAuthor,
  promoHeadline, promoSub, promoB1, promoB2, promoB3, promoCta, promoUrl,
  testimonialText, testimonialName, testimonialRole, testimonialRating,
}) {
  // Dispatch to category-specific canvas.
  if (category === "webinar") {
    return (
      <WebinarCanvas
        layout={webinarLayout}
        slogan={webinarSlogan}
        palette={palette}
        pattern={pattern}
        title={webinarTitle}
        subtitle={webinarSubtitle}
        date={webinarDate}
        time={webinarTime}
        cta={webinarCta}
        b1={webinarBullet1}
        b2={webinarBullet2}
        b3={webinarBullet3}
        stat1Val={webinarStat1Val}
        stat1Lbl={webinarStat1Lbl}
        stat2Val={webinarStat2Val}
        stat2Lbl={webinarStat2Lbl}
        chartStr={webinarChartStr}
        chartLabel={webinarChartLabel}
      />
    );
  }
  if (category === "quote") {
    return (
      <BigQuoteCanvas
        palette={palette}
        pattern={pattern}
        text={bigQuoteText}
        author={bigQuoteAuthor}
      />
    );
  }
  if (category === "promo") {
    return (
      <PromoCanvas
        palette={palette}
        pattern={pattern}
        headline={promoHeadline}
        sub={promoSub}
        b1={promoB1}
        b2={promoB2}
        b3={promoB3}
        cta={promoCta}
        url={promoUrl}
      />
    );
  }
  if (category === "testimonial") {
    return (
      <TestimonialCanvas
        palette={palette}
        pattern={pattern}
        text={testimonialText}
        name={testimonialName}
        role={testimonialRole}
        rating={testimonialRating}
      />
    );
  }
  // ── Performance category (existing logic) ──
  // Hell/Light layout has a completely different structure (light background,
  // top-centered logo, big mixed-case title, dotted curve, 3 pill badges) —
  // render via a separate sub-component to keep the dark canvas code clean.
  if (layout === "light") {
    return (
      <LightCanvas
        palette={palette}
        displayPct={displayPct}
        isNegative={isNegative}
        strategy={strategy}
        modeCfg={modeCfg}
        sparkline={sparkline}
      />
    );
  }
  // Build SVG path for sparkline. Normalize y values. The series is densified
  // with deterministic market noise so the line shows realistic highs & lows.
  const W = 1000, H = layout === "split" ? 400 : 300;
  const dense = densifySeries(sparkline);
  const min = Math.min(...dense);
  const max = Math.max(...dense);
  const range = max - min || 1;
  const padX = 40;
  const padY = 30;
  const stepX = (W - padX * 2) / (dense.length - 1);
  const points = dense.map((v, i) => [
    padX + i * stepX,
    H - padY - ((v - min) / range) * (H - padY * 2),
  ]);
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const gradientId = `grad-${palette.id}`;
  const negativeTint = isNegative ? { from: "#FB7185", to: "#F43F5E", glow: "#F87171" } : palette;

  return (
    <div
      className="w-full h-full relative overflow-hidden font-display"
      style={{ background: "#050816", fontFamily: "var(--font-display, 'Inter', system-ui, sans-serif)" }}
    >
      {/* ── Background pattern layer ────────────────────────── */}
      <BackgroundPattern pattern={pattern} palette={negativeTint} />

      {/* ── Content layer (padded, percentage-based for crisp scaling) ── */}
      <div className="absolute inset-0 flex flex-col" style={{ padding: "7%" }}>
        {/* Top header row: logo (left) + date badge (right) */}
        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <div className="flex items-center" style={{ gap: 18 }}>
              <img src={LOGO_URL} alt="" crossOrigin="anonymous" style={{ height: 90, width: 90 }} />
              <div>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 42, letterSpacing: "0.04em", lineHeight: 1 }}>
                  ALPHALANE AI
                </div>
                <div style={{ color: "#94A3B8", fontWeight: 600, fontSize: 16, letterSpacing: "0.25em", marginTop: 8 }}>
                  LOGIK STATT EMOTION
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              border: `1px solid ${negativeTint.from}55`,
              borderRadius: 20,
              padding: "16px 22px",
              background: "#0B1227aa",
              backdropFilter: "blur(6px)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <Calendar style={{ height: 28, width: 28, color: negativeTint.from }} />
            <div>
              <div style={{ color: negativeTint.from, fontSize: 14, fontWeight: 700, letterSpacing: "0.2em" }}>{modeCfg.badge}</div>
              <div style={{ color: "#fff", fontSize: 19, fontWeight: 600, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{dateRange}</div>
            </div>
          </div>
        </div>

        {/* ── Body: switches by layout ───────────────────────── */}
        <div className="flex-1 flex flex-col justify-center" style={{ marginTop: 56 }}>
          {layout === "classic" && (
            <ClassicLayout
              displayPct={displayPct}
              strategy={strategy}
              negativeTint={negativeTint}
              gradientId={gradientId}
              pathD={pathD}
              points={points}
              W={W}
              H={400}
              modeCfg={modeCfg}
            />
          )}
          {layout === "split" && (
            <SplitLayout
              displayPct={displayPct}
              strategy={strategy}
              negativeTint={negativeTint}
              gradientId={gradientId}
              pathD={pathD}
              points={points}
              W={W}
              H={400}
              modeCfg={modeCfg}
            />
          )}
          {layout === "quote" && (
            <QuoteLayout
              displayPct={displayPct}
              strategy={strategy}
              negativeTint={negativeTint}
              gradientId={gradientId}
              quote={quote}
              pathD={pathD}
              points={points}
              W={W}
              H={280}
              modeCfg={modeCfg}
            />
          )}
        </div>

        {/* ── Bottom trust-badges + wordmark ──────────────────────── */}
        <div
          style={{
            borderRadius: 24,
            border: `1px solid ${negativeTint.from}33`,
            background: "#0B1227aa",
            padding: "28px 32px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 24,
            backdropFilter: "blur(6px)",
          }}
        >
          <TrustItem icon={<ShieldCheck style={{ height: 36, width: 36 }} />} title="KI-STRATEGIE" subtitle="100% DATENBASIERT" color={negativeTint.from} />
          <TrustItem icon={<Target style={{ height: 36, width: 36 }} />} title="DISZIPLINIERT" subtitle="EMOTIONSFREI" color={negativeTint.glow} />
          <TrustItem icon={<BarChart3 style={{ height: 36, width: 36 }} />} title="KONSEQUENT" subtitle="LANGFRISTIG STARK" color={negativeTint.to} />
        </div>

        <div style={{ textAlign: "center", marginTop: 32 }}>
          <div style={{ color: "#fff", fontWeight: 700, letterSpacing: "0.4em", fontSize: 22 }}>LOGIK STATT EMOTION</div>
          <div style={{ color: "#64748B", fontSize: 17, marginTop: 8, letterSpacing: "0.15em" }}>alphalane-tradingsystems.com</div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-Layouts ─────────────────────────────────────────────────────────
function ClassicLayout({ displayPct, strategy, negativeTint, gradientId, pathD, points, W, H, modeCfg }) {
  return (
    <div className="flex-1 flex flex-col" style={{ gap: 36 }}>
      <div style={{ color: "#fff", fontWeight: 800, fontSize: 30, letterSpacing: "0.18em" }}>{modeCfg.title}</div>
      <div
        style={{
          border: `1px solid ${negativeTint.from}33`,
          borderRadius: 32,
          padding: "56px 36px 44px",
          background: "#0B1227aa",
          textAlign: "center",
        }}
      >
        <div style={{ color: negativeTint.from, fontWeight: 700, fontSize: 32, letterSpacing: "0.18em" }}>{modeCfg.metric}</div>
        <BigPercentage value={displayPct} negativeTint={negativeTint} gradientId={gradientId} />
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginTop: 12 }}>
          <TrendingUp style={{ height: 28, width: 28, color: negativeTint.from }} />
          <span style={{ color: "#94A3B8", fontWeight: 700, fontSize: 20, letterSpacing: "0.18em" }}>STRATEGIE:</span>
          <span style={{ color: "#fff", fontWeight: 800, fontSize: 22, letterSpacing: "0.1em" }}>{strategy}</span>
        </div>
      </div>
      <SparklineSvg pathD={pathD} points={points} W={W} H={H} negativeTint={negativeTint} gradientId={gradientId} />
    </div>
  );
}

// "Minimal" layout — focuses fully on the percentage. No chart, no card —
// just gigantic gradient number + strategy line. Looks great on Story format
// where attention is limited.
function SplitLayout({ displayPct, strategy, negativeTint, gradientId, modeCfg }) {
  return (
    <div className="flex-1 flex flex-col justify-center" style={{ gap: 40, textAlign: "center" }}>
      <div style={{ color: negativeTint.from, fontWeight: 700, fontSize: 34, letterSpacing: "0.25em" }}>{modeCfg.metric}</div>
      <BigPercentage value={displayPct} negativeTint={negativeTint} gradientId={gradientId} huge />
      <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 14 }}>
        <TrendingUp style={{ height: 32, width: 32, color: negativeTint.from }} />
        <span style={{ color: "#94A3B8", fontWeight: 700, fontSize: 22, letterSpacing: "0.22em" }}>STRATEGIE:</span>
        <span style={{ color: "#fff", fontWeight: 800, fontSize: 24, letterSpacing: "0.12em" }}>{strategy}</span>
      </div>
    </div>
  );
}

function QuoteLayout({ displayPct, strategy, negativeTint, gradientId, quote, pathD, points, W, H, modeCfg }) {
  return (
    <div className="flex flex-col" style={{ gap: 40 }}>
      <div
        style={{
          border: `1px solid ${negativeTint.from}33`,
          borderRadius: 32,
          padding: "48px 36px",
          background: "#0B1227aa",
          textAlign: "center",
        }}
      >
        <div style={{ color: negativeTint.from, fontWeight: 700, fontSize: 30, letterSpacing: "0.22em" }}>{modeCfg.metric}</div>
        <BigPercentage value={displayPct} negativeTint={negativeTint} gradientId={gradientId} />
      </div>
      <div
        style={{
          textAlign: "center",
          padding: "32px 36px",
          borderLeft: `5px solid ${negativeTint.from}`,
          borderRight: `5px solid ${negativeTint.to}`,
        }}
      >
        <Quote style={{ height: 48, width: 48, color: negativeTint.from, margin: "0 auto" }} />
        <div style={{ color: "#fff", fontSize: 38, fontWeight: 700, fontStyle: "italic", marginTop: 14, lineHeight: 1.3 }}>
          „{quote}&rdquo;
        </div>
        <div style={{ color: "#64748B", fontWeight: 700, fontSize: 18, letterSpacing: "0.18em", marginTop: 18 }}>
          STRATEGIE: <span style={{ color: "#fff" }}>{strategy}</span>
        </div>
      </div>
      <SparklineSvg pathD={pathD} points={points} W={W} H={H} negativeTint={negativeTint} gradientId={gradientId} compact />
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────
function BigPercentage({ value, negativeTint, gradientId, huge }) {
  // Wider viewBox + slightly tamer font sizes so even long values like
  // "+12,34%" don't clip on the left/right edges (the SVG hard-clips at
  // its viewBox bounds; we use textLength as belt-and-suspenders).
  const fontSize = huge ? 220 : 200;
  return (
    <svg viewBox="0 0 1000 320" style={{ width: "100%", height: "auto", margin: "16px 0" }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={negativeTint.from} />
          <stop offset="100%" stopColor={negativeTint.to} />
        </linearGradient>
      </defs>
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill={`url(#${gradientId})`}
        lengthAdjust="spacingAndGlyphs"
        textLength={huge ? 900 : 800}
        style={{
          fontFamily: "var(--font-display, 'Inter', system-ui, sans-serif)",
          fontWeight: 900,
          fontSize: fontSize,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </text>
    </svg>
  );
}

function SparklineSvg({ pathD, points, W, H, negativeTint, gradientId, compact }) {
  const areaPath = `${pathD} L ${points[points.length - 1][0]} ${H} L ${points[0][0]} ${H} Z`;
  const lineGradId = `line-${gradientId}`;
  const areaGradId = `area-${gradientId}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", maxHeight: compact ? 220 : 360 }}>
      <defs>
        <linearGradient id={lineGradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={negativeTint.from} />
          <stop offset="100%" stopColor={negativeTint.to} />
        </linearGradient>
        <linearGradient id={areaGradId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={negativeTint.glow} stopOpacity="0.35" />
          <stop offset="100%" stopColor={negativeTint.glow} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Faint horizontal grid */}
      {[0.25, 0.5, 0.75].map((y) => (
        <line key={y} x1="0" y1={H * y} x2={W} y2={H * y} stroke="#1E293B" strokeWidth="1.5" strokeDasharray="3 9" />
      ))}
      {/* Area fill */}
      <path d={areaPath} fill={`url(#${areaGradId})`} />
      {/* Line */}
      <path d={pathD} fill="none" stroke={`url(#${lineGradId})`} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots — only start + end (line is densified with market noise) */}
      {[points[0], points[points.length - 1]].map(([x, y], i) => (
        <circle key={i === 0 ? "start" : "end"} cx={x} cy={y} r="11" fill={i === 1 ? negativeTint.to : negativeTint.from} />
      ))}
    </svg>
  );
}

function TrustItem({ icon, title, subtitle, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ color, display: "flex" }}>{icon}</div>
      <div>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: "0.18em", lineHeight: 1.1 }}>{title}</div>
        <div style={{ color: "#94A3B8", fontWeight: 600, fontSize: 13, letterSpacing: "0.18em", marginTop: 5 }}>{subtitle}</div>
      </div>
    </div>
  );
}

function BackgroundPattern({ pattern, palette }) {
  if (pattern === "dots") {
    return (
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 25%, ${palette.glow}33 0%, transparent 45%), radial-gradient(circle at 85% 75%, ${palette.to}22 0%, transparent 45%), radial-gradient(${palette.from}22 1px, transparent 1px)`,
          backgroundSize: "auto, auto, 28px 28px",
        }}
      />
    );
  }
  if (pattern === "glow") {
    return (
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 30% 20%, ${palette.from}38 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, ${palette.to}38 0%, transparent 55%),
            radial-gradient(ellipse at 50% 50%, ${palette.glow}15 0%, transparent 70%)
          `,
        }}
      />
    );
  }
  if (pattern === "grid") {
    return (
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(ellipse at 50% 0%, ${palette.glow}30 0%, transparent 55%), linear-gradient(${palette.from}14 1px, transparent 1px), linear-gradient(90deg, ${palette.from}14 1px, transparent 1px)`,
          backgroundSize: "auto, 72px 72px, 72px 72px",
        }}
      />
    );
  }
  if (pattern === "aurora") {
    return (
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 40% at 18% 6%, ${palette.from}45 0%, transparent 62%),
            radial-gradient(ellipse 70% 35% at 88% 28%, ${palette.to}38 0%, transparent 60%),
            radial-gradient(ellipse 95% 45% at 50% 100%, ${palette.glow}30 0%, transparent 62%)
          `,
        }}
      />
    );
  }
  // mesh
  return (
    <div
      className="absolute inset-0"
      style={{
        background: `
          conic-gradient(from 180deg at 50% 50%, ${palette.from}22 0deg, transparent 90deg, ${palette.to}22 180deg, transparent 270deg, ${palette.from}22 360deg),
          radial-gradient(ellipse at 50% 0%, ${palette.glow}30 0%, transparent 50%)
        `,
      }}
    />
  );
}


// ── Light Layout (separate canvas — pastel background, mixed-case title) ──
function LightCanvas({ palette, displayPct, isNegative, strategy, modeCfg, sparkline }) {
  // Negative values still get a warning tint, but kept rosé so it doesn't
  // clash with the soft pastel background.
  const tint = isNegative
    ? { from: "#F87171", to: "#EC4899", glow: "#FB7185" }
    : palette;

  // Subtle dotted-arc decoration confined to the BOTTOM-LEFT corner so it
  // never collides with the centred big-% text. We render 7 progressively
  // smaller dots arranged in a quarter-circle hugging the corner.
  const dotCount = 7;
  const arcDots = Array.from({ length: dotCount }, (_, i) => {
    const cx = 80, cy = 1640, r = 180;
    const t = i / (dotCount - 1);
    // Quarter circle from straight-up (angle = -90°) to straight-right (0°).
    const theta = -Math.PI / 2 + t * (Math.PI / 2);
    const x = cx + Math.cos(theta) * r;
    const y = cy + Math.sin(theta) * r;
    const size = 16 - t * 9;       // 16 → 7px
    const opacity = 0.55 - t * 0.4; // 0.55 → 0.15
    return { x, y, r: size, opacity };
  });

  const gradId = `light-pct-${tint.from.replace("#", "")}`;
  const pillStyle = {
    border: `1.5px solid ${tint.from}`,
    borderRadius: 999,
    padding: "22px 24px",
    background: "rgba(255,255,255,0.55)",
    color: "#1E293B",
    fontWeight: 600,
    fontSize: 22,
    fontFamily: "var(--font-display, 'Inter', system-ui, sans-serif)",
    textAlign: "center",
    backdropFilter: "blur(4px)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        fontFamily: "var(--font-display, 'Inter', system-ui, sans-serif)",
        background: "linear-gradient(135deg, #DEE4FF 0%, #E8DFFA 50%, #F4E8FA 100%)",
      }}
    >
      {/* Soft palette-tinted glow overlays so the brand colour still bleeds through */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 20% 15%, ${tint.from}22 0%, transparent 55%),
            radial-gradient(ellipse at 85% 80%, ${tint.to}1f 0%, transparent 55%)
          `,
        }}
      />

      {/* Decorative dotted arc on the left */}
      <svg
        width="1080"
        height="1920"
        viewBox="0 0 1080 1920"
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        aria-hidden="true"
      >
        {arcDots.map((d, i) => (
          <circle
            key={`${d.x}-${d.y}`}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill={tint.from}
            opacity={d.opacity}
          />
        ))}
      </svg>

      {/* Content — column layout, top-aligned, with generous breathing room */}
      <div
        className="absolute inset-0 flex flex-col items-center"
        style={{ padding: "8% 7% 7%" }}
      >
        {/* Top: centered logo + tagline */}
        <div style={{ textAlign: "center", marginTop: 30 }}>
          <img
            src={LOGO_URL}
            alt=""
            crossOrigin="anonymous"
            style={{ height: 170, width: 170, margin: "0 auto", display: "block" }}
          />
          <div
            style={{
              color: "#0F172A",
              fontWeight: 800,
              fontSize: 42,
              letterSpacing: "0.16em",
              marginTop: 22,
            }}
          >
            LOGIK STATT EMOTION
          </div>
        </div>

        {/* Title: mixed-case Wochenperformance / Monatsperformance */}
        <div
          style={{
            marginTop: 80,
            color: "#0F172A",
            fontWeight: 800,
            fontSize: 82,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
            textAlign: "center",
            width: "100%",
            wordBreak: "break-word",
          }}
        >
          {modeCfg.titleMixed}
        </div>

        {/* Big gradient percentage — wider viewBox + smaller textLength so
            even longer values like "+12,34%" stay safely inside the SVG */}
        <div style={{ marginTop: "auto", marginBottom: "auto", width: "100%" }}>
          <svg viewBox="0 0 1200 360" style={{ width: "100%", height: "auto" }} preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={tint.from} />
                <stop offset="100%" stopColor={tint.to} />
              </linearGradient>
            </defs>
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="central"
              fill={`url(#${gradId})`}
              lengthAdjust="spacingAndGlyphs"
              textLength={820}
              style={{
                fontFamily: "var(--font-display, 'Inter', system-ui, sans-serif)",
                fontWeight: 900,
                fontSize: 240,
                letterSpacing: "-0.02em",
              }}
            >
              {displayPct}
            </text>
          </svg>
          <div
            style={{
              textAlign: "center",
              color: "#475569",
              fontSize: 36,
              fontWeight: 500,
              marginTop: 10,
            }}
          >
            {modeCfg.metricMixed} ({strategy === "ALPHA AI" ? "KI-Strategie" : strategy})
          </div>
        </div>

        {/* Bottom: 3 pill badges */}
        <div
          style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 22,
            marginTop: 24,
          }}
        >
          <div data-testid="light-pill-strategy" style={pillStyle}>
            {strategy === "ALPHA AI" ? "KI-Strategie" : strategy}
          </div>
          <div data-testid="light-pill-return" style={pillStyle}>
            {displayPct}
          </div>
          <div data-testid="light-pill-domain" style={pillStyle}>
            alphalane-ai.com
          </div>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// Category forms (Webinar / Quote / Promo / Testimonial)
// ════════════════════════════════════════════════════════════════════════

const LABEL_CLS = "text-[11px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5 mb-1.5";
const INPUT_CLS = "w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400";

function WebinarForm({
  webinarList, webinarLoading, webinarId, setWebinarId, applyWebinar,
  webinarTitle, setWebinarTitle, webinarSubtitle, setWebinarSubtitle,
  webinarDate, setWebinarDate, webinarTime, setWebinarTime,
  webinarCta, setWebinarCta,
  webinarBullet1, setWebinarBullet1,
  webinarBullet2, setWebinarBullet2,
  webinarBullet3, setWebinarBullet3,
  webinarStat1Val, setWebinarStat1Val, webinarStat1Lbl, setWebinarStat1Lbl,
  webinarStat2Val, setWebinarStat2Val, webinarStat2Lbl, setWebinarStat2Lbl,
  webinarChartStr, setWebinarChartStr, webinarChartLabel, setWebinarChartLabel,
  webinarSlogan, setWebinarSlogan,
}) {
  return (
    <div className="space-y-4" data-testid="webinar-form">
      <div>
        <label className="block">
          <span className={LABEL_CLS}><Users className="h-3.5 w-3.5" /> Webinar auswählen</span>
          {webinarLoading ? (
            <div className={INPUT_CLS + " text-slate-400 italic"}>Lade Webinare…</div>
          ) : webinarList.length === 0 ? (
            <div className={INPUT_CLS + " text-slate-400 italic"}>Keine kommenden Webinare gefunden.</div>
          ) : (
            <select
              value={webinarId}
              onChange={(e) => {
                const w = webinarList.find((w) => w.id === e.target.value);
                if (w) applyWebinar(w);
              }}
              data-testid="webinar-select"
              className={INPUT_CLS + " font-mono-tab"}
            >
              {webinarList.map((w) => (
                <option key={w.id} value={w.id}>
                  {new Date(w.scheduled_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit" })} · {w.title}
                </option>
              ))}
            </select>
          )}
          <p className="mt-1 text-[11px] text-slate-500">Auswahl füllt Titel, Datum und Bullets automatisch.</p>
        </label>
      </div>

      <FieldText label="Titel" icon={Megaphone} value={webinarTitle} onChange={setWebinarTitle} testid="webinar-title" />
      <FieldText label="Untertitel / Speaker-Zeile" icon={MessageSquare} value={webinarSubtitle} onChange={setWebinarSubtitle} testid="webinar-subtitle" />
      <FieldText label="Datum (Anzeigetext)" icon={Calendar} value={webinarDate} onChange={setWebinarDate} testid="webinar-date" placeholder="z.B. Sonntag, 14. Juni 2026" />
      <FieldText label="Uhrzeit (Anzeigetext)" icon={Clock} value={webinarTime} onChange={setWebinarTime} testid="webinar-time" placeholder="z.B. 20:00 Uhr Schweizer Zeit" />
      <FieldText label="Bullet 1" icon={CheckCircle2} value={webinarBullet1} onChange={setWebinarBullet1} testid="webinar-bullet-1" />
      <FieldText label="Bullet 2" icon={CheckCircle2} value={webinarBullet2} onChange={setWebinarBullet2} testid="webinar-bullet-2" />
      <FieldText label="Bullet 3" icon={CheckCircle2} value={webinarBullet3} onChange={setWebinarBullet3} testid="webinar-bullet-3" />
      <FieldText label="Call-to-Action (Badge)" icon={Rocket} value={webinarCta} onChange={setWebinarCta} testid="webinar-cta" />

      {/* ── Slogan (für Layouts ohne Portraits: Slogan / Terminal) ── */}
      <div>
        <FieldText label="Slogan / Zitat (Layouts: Slogan, Terminal)" icon={Quote} value={webinarSlogan} onChange={setWebinarSlogan} testid="webinar-slogan" />
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {QUOTES.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setWebinarSlogan(q)}
              data-testid={`webinar-slogan-preset-${q.slice(0, 10)}`}
              className="px-2.5 py-1.5 rounded-lg text-[11px] font-display font-semibold border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50 transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {/* ── Performance-Daten (auto-fetched, manuell überschreibbar) ── */}
      <div className="pt-3 mt-2 border-t border-slate-200">
        <div className={LABEL_CLS + " !text-violet-600"}>
          <TrendingUp className="h-3.5 w-3.5" /> Performance-Daten (Auto-Fetch aus Track Record)
        </div>
        <p className="text-[11px] text-slate-500 mb-3">Werden automatisch geladen — hier optional überschreiben.</p>
        <div className="grid grid-cols-2 gap-3">
          <FieldText label="KPI 1 Wert" icon={TrendingUp} value={webinarStat1Val} onChange={setWebinarStat1Val} testid="webinar-stat1-val" />
          <FieldText label="KPI 1 Label" icon={MessageSquare} value={webinarStat1Lbl} onChange={setWebinarStat1Lbl} testid="webinar-stat1-lbl" />
          <FieldText label="KPI 2 Wert" icon={TrendingUp} value={webinarStat2Val} onChange={setWebinarStat2Val} testid="webinar-stat2-val" />
          <FieldText label="KPI 2 Label" icon={MessageSquare} value={webinarStat2Lbl} onChange={setWebinarStat2Lbl} testid="webinar-stat2-lbl" />
        </div>
        <div className="mt-3">
          <label className="block">
            <span className={LABEL_CLS}><BarChart3 className="h-3.5 w-3.5" /> Chart-Punkte (kumulierte % — Komma-getrennt)</span>
            <textarea
              value={webinarChartStr}
              onChange={(e) => setWebinarChartStr(e.target.value)}
              data-testid="webinar-chart-str"
              rows={2}
              className={INPUT_CLS + " font-mono-tab text-xs resize-none"}
              placeholder="47, 111, 147, 202, 248, 282, 401, 287, 383"
            />
            <p className="mt-1 text-[11px] text-slate-500">Leer lassen um Chart auszublenden.</p>
          </label>
        </div>
        <div className="mt-2">
          <FieldText label="Chart-Label" icon={MessageSquare} value={webinarChartLabel} onChange={setWebinarChartLabel} testid="webinar-chart-label" />
        </div>
      </div>
    </div>
  );
}

function QuoteForm({ bigQuoteText, setBigQuoteText, bigQuoteAuthor, setBigQuoteAuthor }) {
  return (
    <div className="space-y-4" data-testid="quote-form">
      <div>
        <label className="block">
          <span className={LABEL_CLS}><Quote className="h-3.5 w-3.5" /> Zitat-Text</span>
          <textarea
            value={bigQuoteText}
            onChange={(e) => setBigQuoteText(e.target.value)}
            data-testid="quote-text"
            rows={4}
            className={INPUT_CLS + " resize-none"}
            placeholder="Logik statt Emotion."
          />
          <p className="mt-1 text-[11px] text-slate-500">Kurz und prägnant — max. ~80 Zeichen für beste Lesbarkeit.</p>
        </label>
      </div>
      <FieldText label="Autor / Marke" icon={Sparkles} value={bigQuoteAuthor} onChange={setBigQuoteAuthor} testid="quote-author" />
      <div className="space-y-1.5 pt-1">
        <span className={LABEL_CLS}><Sparkles className="h-3.5 w-3.5" /> Schnellauswahl</span>
        <div className="flex flex-wrap gap-1.5">
          {QUOTES.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setBigQuoteText(q)}
              data-testid={`quote-preset-${q.slice(0, 10)}`}
              className="px-2.5 py-1.5 rounded-lg text-[11px] font-display font-semibold border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50 transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PromoForm({
  promoPresetIdx, applyPromoPreset,
  promoHeadline, setPromoHeadline, promoSub, setPromoSub,
  promoB1, setPromoB1, promoB2, setPromoB2, promoB3, setPromoB3,
  promoCta, setPromoCta, promoUrl, setPromoUrl,
}) {
  return (
    <div className="space-y-4" data-testid="promo-form">
      <div>
        <span className={LABEL_CLS}><Megaphone className="h-3.5 w-3.5" /> Preset wählen</span>
        <div className="grid grid-cols-2 gap-1.5">
          {PROMO_PRESETS.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPromoPreset(i)}
              data-testid={`promo-preset-${p.id}`}
              className={`px-2 py-2 rounded-lg text-[11px] font-display font-semibold border transition-colors ${
                promoPresetIdx === i ? "border-violet-400 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              {p.headline}
            </button>
          ))}
        </div>
      </div>
      <FieldText label="Headline" icon={Megaphone} value={promoHeadline} onChange={setPromoHeadline} testid="promo-headline" />
      <FieldText label="Subline" icon={MessageSquare} value={promoSub} onChange={setPromoSub} testid="promo-sub" />
      <FieldText label="Bullet 1" icon={CheckCircle2} value={promoB1} onChange={setPromoB1} testid="promo-b1" />
      <FieldText label="Bullet 2" icon={CheckCircle2} value={promoB2} onChange={setPromoB2} testid="promo-b2" />
      <FieldText label="Bullet 3" icon={CheckCircle2} value={promoB3} onChange={setPromoB3} testid="promo-b3" />
      <FieldText label="Call-to-Action" icon={Rocket} value={promoCta} onChange={setPromoCta} testid="promo-cta" />
      <FieldText label="URL / Domain" icon={ArrowRight} value={promoUrl} onChange={setPromoUrl} testid="promo-url" />
    </div>
  );
}

function TestimonialForm({
  testimonialIdx, applyTestimonialPreset,
  testimonialText, setTestimonialText,
  testimonialName, setTestimonialName,
  testimonialRole, setTestimonialRole,
  testimonialRating, setTestimonialRating,
}) {
  return (
    <div className="space-y-4" data-testid="testimonial-form">
      <div>
        <span className={LABEL_CLS}><Award className="h-3.5 w-3.5" /> Preset wählen</span>
        <div className="grid grid-cols-1 gap-1.5">
          {TESTIMONIALS.map((t, i) => (
            <button
              key={t.name}
              type="button"
              onClick={() => applyTestimonialPreset(i)}
              data-testid={`testimonial-preset-${i}`}
              className={`px-3 py-2 rounded-lg text-left text-[12px] font-display border transition-colors ${
                testimonialIdx === i ? "border-violet-400 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <strong className="block text-[11px] uppercase tracking-wide mb-0.5">{t.name}</strong>
              <span className="text-slate-500 line-clamp-2">{t.text}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block">
          <span className={LABEL_CLS}><Quote className="h-3.5 w-3.5" /> Zitat</span>
          <textarea
            value={testimonialText}
            onChange={(e) => setTestimonialText(e.target.value)}
            data-testid="testimonial-text"
            rows={4}
            className={INPUT_CLS + " resize-none"}
          />
        </label>
      </div>
      <FieldText label="Name" icon={Award} value={testimonialName} onChange={setTestimonialName} testid="testimonial-name" />
      <FieldText label="Rolle / Kunden-seit" icon={Clock} value={testimonialRole} onChange={setTestimonialRole} testid="testimonial-role" />
      <div>
        <span className={LABEL_CLS}><Star className="h-3.5 w-3.5" /> Bewertung</span>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setTestimonialRating(n)}
              data-testid={`testimonial-star-${n}`}
              className="p-0.5"
            >
              <Star className={`h-7 w-7 transition-colors ${n <= testimonialRating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FieldText({ label, icon: Icon, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="block">
        <span className={LABEL_CLS}><Icon className="h-3.5 w-3.5" /> {label}</span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          className={INPUT_CLS}
        />
      </label>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Category canvases (Webinar / BigQuote / Promo / Testimonial)
// Each renders at exactly 1080×1920 → captured by html-to-image.
// ════════════════════════════════════════════════════════════════════════

const FONT_DISPLAY = "var(--font-display, 'Inter', system-ui, sans-serif)";

// Portrait URLs served from /public. Same-origin so html-to-image can embed them.
const PORTRAIT_PASCAL = "/pascal-avatar.png";
const PORTRAIT_ADRIAN = "/adrian-avatar.png";

// Shared: Alphalane top header (logo + wordmark + optional LIVE pill)
function WebinarHeader({ palette, showLive = true, centered = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18, justifyContent: centered ? "center" : "flex-start" }}>
      <img src={LOGO_URL} alt="" crossOrigin="anonymous" style={{ height: 82, width: 82 }} />
      <div style={{ flex: centered ? "0 0 auto" : 1 }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 34, letterSpacing: "0.03em", lineHeight: 1 }}>
          ALPHALANE AI
        </div>
        <div style={{ color: "#94A3B8", fontWeight: 600, fontSize: 13, letterSpacing: "0.24em", marginTop: 4 }}>
          LOGIK STATT EMOTION
        </div>
      </div>
      {showLive && !centered && (
        <div
          style={{
            padding: "8px 16px",
            borderRadius: 999,
            border: `1.5px solid ${palette.from}`,
            color: palette.from,
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: "0.25em",
            background: `${palette.from}18`,
          }}
        >
          ● LIVE
        </div>
      )}
    </div>
  );
}

// Shared: Circular speaker portrait with animated glow ring.
function SpeakerPortrait({ src, name, size = 240, palette }) {
  const ring = size + 22;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
      <div style={{ position: "relative", height: ring, width: ring, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {/* Soft circular halo — pure radial gradient (no blur filter → exports cleanly to PNG) */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            height: ring + Math.round(size * 0.6),
            width: ring + Math.round(size * 0.6),
            borderRadius: "50%",
            background: `radial-gradient(circle, ${palette.glow}5e 0%, ${palette.from}3d 34%, ${palette.to}1f 50%, transparent 68%)`,
            pointerEvents: "none",
          }}
        />
        {/* Crisp gradient ring */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: `conic-gradient(from 0deg, ${palette.from}, ${palette.to}, ${palette.glow}, ${palette.from})`,
          }}
        />
        {/* Inner dark ring */}
        <div
          style={{
            position: "absolute",
            inset: 6,
            borderRadius: "50%",
            background: "#050816",
          }}
        />
        {/* Portrait image */}
        <img
          src={src}
          alt={name}
          crossOrigin="anonymous"
          style={{
            position: "relative",
            height: size,
            width: size,
            borderRadius: "50%",
            objectFit: "cover",
            border: `3px solid ${palette.from}88`,
          }}
        />
      </div>
      {name && (
        <div
          style={{
            padding: "8px 26px",
            borderRadius: 999,
            border: `1.5px solid ${palette.from}66`,
            background: `${palette.from}18`,
            color: "#fff",
            fontWeight: 800,
            fontSize: 22,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
          }}
        >
          {name}
        </div>
      )}
    </div>
  );
}

// Shared: Compact date/time card with calendar + clock icons (used by duo + minimal layouts).
function WebinarDateTimeCard({ date, time, palette, big = false }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: big ? 18 : 12,
        padding: big ? "32px 40px" : "22px 30px",
        borderRadius: 28,
        background: `linear-gradient(135deg, ${palette.from}18, ${palette.to}10)`,
        border: `1.5px solid ${palette.from}55`,
        boxShadow: `0 20px 60px ${palette.from}22, inset 0 1px 0 ${palette.from}22`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <div
          style={{
            background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
            borderRadius: 14,
            padding: 14,
            display: "flex",
          }}
        >
          <Calendar style={{ height: big ? 44 : 34, width: big ? 44 : 34, color: "#fff" }} />
        </div>
        <div style={{ color: "#fff", fontWeight: 900, fontSize: big ? 48 : 34, lineHeight: 1.05, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
          {date || "Termin folgt"}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <div
          style={{
            background: `${palette.from}22`,
            border: `1.5px solid ${palette.from}44`,
            borderRadius: 14,
            padding: 14,
            display: "flex",
          }}
        >
          <Clock style={{ height: big ? 40 : 30, width: big ? 40 : 30, color: palette.from }} />
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <div
            style={{
              color: palette.from,
              fontWeight: 900,
              fontSize: big ? 42 : 32,
              letterSpacing: "0.02em",
              fontFeatureSettings: "'tnum'",
            }}
          >
            {(time || "").split(" ")[0]}
          </div>
          <div style={{ color: "#CBD5E1", fontWeight: 600, fontSize: big ? 22 : 18, letterSpacing: "0.16em", textTransform: "uppercase" }}>
            {(time || "").split(" ").slice(1).join(" ")}
          </div>
        </div>
      </div>
    </div>
  );
}

// Dispatcher — picks a sub-canvas based on the chosen `layout`.
function WebinarCanvas({ layout, ...props }) {
  if (layout === "duo") return <WebinarCanvasDuo {...props} />;
  if (layout === "minimal") return <WebinarCanvasMinimal {...props} />;
  if (layout === "spotlight") return <WebinarCanvasSpotlight {...props} />;
  if (layout === "ticket") return <WebinarCanvasTicket {...props} />;
  if (layout === "slogan") return <WebinarCanvasSlogan {...props} />;
  if (layout === "stat") return <WebinarCanvasStat {...props} />;
  if (layout === "terminal") return <WebinarCanvasTerminal {...props} />;
  return <WebinarCanvasPerformance {...props} />;
}

// ── Layout 1: "Duo" ─────────────────────────────────────────────────────
// Reference image style: centered logo, big title, two portrait circles side
// by side with glowing rings + name pills, big date/time card at bottom.
function WebinarCanvasDuo({ palette, pattern, title, subtitle, date, time, cta, b1, b2, b3 }) {
  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{ background: "#050816", fontFamily: FONT_DISPLAY }}
    >
      <BackgroundPattern pattern={pattern} palette={palette} />
      <div className="absolute inset-0 flex flex-col items-center" style={{ padding: "6% 6%" }}>
        <WebinarHeader palette={palette} showLive={false} centered />

        <h1
          style={{
            color: "#fff",
            fontWeight: 900,
            fontSize: 132,
            letterSpacing: "-0.035em",
            lineHeight: 0.94,
            marginTop: 44,
            textAlign: "center",
          }}
        >
          {title}
        </h1>

        <div style={{ color: "#CBD5E1", fontSize: 28, fontWeight: 500, lineHeight: 1.35, marginTop: 22, maxWidth: "88%", textAlign: "center" }}>
          {subtitle}
        </div>

        {/* Two portraits centered, connected by a soft glow line */}
        <div style={{ position: "relative", marginTop: 60, display: "flex", alignItems: "center", justifyContent: "center", gap: 48 }}>
          {/* Center glow flare */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "38%",
              transform: "translate(-50%, -50%)",
              width: 560,
              height: 140,
              background: `radial-gradient(ellipse at center, ${palette.from}50, transparent 68%)`,
              pointerEvents: "none",
            }}
          />
          <SpeakerPortrait src={PORTRAIT_PASCAL} name="Pascal" palette={palette} size={340} />
          <SpeakerPortrait src={PORTRAIT_ADRIAN} name="Adrian" palette={palette} size={340} />
        </div>

        {/* Bullets — fill the vertical space between portraits and date card */}
        {[b1, b2, b3].filter(Boolean).length > 0 && (
          <div style={{ marginTop: 64, display: "flex", flexDirection: "column", gap: 20, alignItems: "flex-start" }}>
            {[b1, b2, b3].filter(Boolean).map((b, i) => (
              <div key={typeof b === 'string' ? b : i} style={{ display: "flex", alignItems: "center", gap: 18 }}>
                <div
                  style={{
                    height: 42,
                    width: 42,
                    borderRadius: 12,
                    background: `${palette.from}26`,
                    border: `1.5px solid ${palette.from}66`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle2 style={{ height: 24, width: 24, color: palette.from }} />
                </div>
                <div style={{ color: "#E2E8F0", fontSize: 27, fontWeight: 600 }}>{b}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 32 }}>
          <WebinarDateTimeCard date={date} time={time} palette={palette} big />
          <div
            style={{
              borderRadius: 999,
              padding: "24px 60px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 900,
              fontSize: 30,
              letterSpacing: "0.22em",
              textAlign: "center",
              boxShadow: `0 20px 70px ${palette.from}66`,
            }}
          >
            {cta} →
          </div>
          <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            alphalane-tradingsystems.com
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Layout 2: "Performance Story" ────────────────────────────────────────
// Data-first: header w/ mini speaker pill, title, subtitle, date/time pill,
// glow-line chart, 2 KPI tiles, bullets, CTA. This is the original design
// enriched with a small Pascal + Adrian avatar strip below the subtitle.
function WebinarCanvasPerformance({ palette, pattern, title, subtitle, date, time, cta, b1, b2, b3,
                                    stat1Val, stat1Lbl, stat2Val, stat2Lbl, chartStr, chartLabel }) {
  const series = (chartStr || "")
    .split(/[,;]/)
    .map((x) => parseFloat(x.trim().replace(",", ".")))
    .filter((n) => Number.isFinite(n));

  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: "#050816", fontFamily: FONT_DISPLAY }}>
      <BackgroundPattern pattern={pattern} palette={palette} />

      <div className="absolute inset-0 flex flex-col" style={{ padding: "6% 6%" }}>
        <WebinarHeader palette={palette} />

        <h1
          style={{
            color: "#fff",
            fontWeight: 900,
            fontSize: 108,
            letterSpacing: "-0.035em",
            lineHeight: 0.94,
            marginTop: 44,
          }}
        >
          {title}
        </h1>

        <div style={{ color: "#CBD5E1", fontSize: 24, fontWeight: 500, lineHeight: 1.35, marginTop: 18, maxWidth: "92%" }}>
          {subtitle}
        </div>

        {/* Compact speaker strip (Pascal + Adrian) */}
        <div style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 22 }}>
          <div style={{ display: "flex" }}>
            <img
              src={PORTRAIT_PASCAL}
              alt="Pascal"
              crossOrigin="anonymous"
              style={{ height: 78, width: 78, borderRadius: "50%", objectFit: "cover", border: `3px solid ${palette.from}`, boxShadow: `0 0 30px ${palette.from}55` }}
            />
            <img
              src={PORTRAIT_ADRIAN}
              alt="Adrian"
              crossOrigin="anonymous"
              style={{ height: 78, width: 78, borderRadius: "50%", objectFit: "cover", border: `3px solid ${palette.to}`, boxShadow: `0 0 30px ${palette.to}55`, marginLeft: -18 }}
            />
          </div>
          <div style={{ color: "#E2E8F0", fontWeight: 700, fontSize: 20, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            Pascal Schwab · Adrian Bienz
          </div>
        </div>

        {/* Date/time pill */}
        <div
          style={{
            marginTop: 24,
            display: "inline-flex",
            alignSelf: "flex-start",
            alignItems: "center",
            gap: 14,
            padding: "14px 22px",
            borderRadius: 22,
            background: `linear-gradient(135deg, ${palette.from}22, ${palette.to}22)`,
            border: `1.5px solid ${palette.from}66`,
          }}
        >
          <div style={{ background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`, borderRadius: 12, padding: 10, display: "flex" }}>
            <Calendar style={{ height: 30, width: 30, color: "#fff" }} />
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 24, lineHeight: 1.15 }}>{date || "Termin folgt"}</div>
            <div style={{ color: "#CBD5E1", fontSize: 17, marginTop: 2 }}>{time}</div>
          </div>
        </div>

        {series.length >= 2 && (
          <div
            style={{
              marginTop: 28,
              borderRadius: 28,
              padding: 26,
              background: "#0B1227aa",
              border: `1.5px solid ${palette.from}33`,
              backdropFilter: "blur(6px)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <div style={{ color: "#94A3B8", fontSize: 13, letterSpacing: "0.2em", fontWeight: 700, textTransform: "uppercase" }}>
                {chartLabel || "Track Record"}
              </div>
              <div style={{ color: palette.from, fontSize: 15, fontWeight: 700 }}>
                +{Math.max(...series).toFixed(0)}%
              </div>
            </div>
            <MiniSparkChart series={series} palette={palette} />
          </div>
        )}

        <div style={{ marginTop: 22, display: "flex", gap: 18 }}>
          {[
            [stat1Val, stat1Lbl],
            [stat2Val, stat2Lbl],
          ].map(([val, lbl], i) => (
            <div
              key={lbl}
              style={{
                flex: 1,
                borderRadius: 26,
                padding: "26px 22px",
                background: `linear-gradient(180deg, ${palette.from}22, ${palette.to}0c)`,
                border: `1.5px solid ${palette.from}55`,
                textAlign: "center",
                boxShadow: `inset 0 1px 0 ${palette.from}22`,
              }}
            >
              <div
                style={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 58,
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                  fontFeatureSettings: "'tnum'",
                  background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {val}
              </div>
              <div style={{ color: "#94A3B8", fontSize: 13, letterSpacing: "0.18em", fontWeight: 700, textTransform: "uppercase", marginTop: 12 }}>
                {lbl}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {[b1, b2, b3].filter(Boolean).map((b, i) => (
            <div key={typeof b === 'string' ? b : i} style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  height: 34,
                  width: 34,
                  borderRadius: 10,
                  background: `${palette.from}26`,
                  border: `1.5px solid ${palette.from}66`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CheckCircle2 style={{ height: 20, width: 20, color: palette.from }} />
              </div>
              <div style={{ color: "#E2E8F0", fontSize: 22, fontWeight: 500 }}>{b}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "auto", paddingTop: 24 }}>
          <div
            style={{
              borderRadius: 999,
              padding: "22px 36px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 800,
              fontSize: 27,
              letterSpacing: "0.18em",
              textAlign: "center",
              boxShadow: `0 14px 50px ${palette.from}55`,
            }}
          >
            {cta} →
          </div>
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.15em" }}>alphalane-tradingsystems.com</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Layout 3: "Minimal Announce" ─────────────────────────────────────────
// Portraits stacked vertically, big centered title, date/time card, CTA.
// No chart, no KPIs — pure announcement feel; works even without data.
function WebinarCanvasMinimal({ palette, pattern, title, subtitle, date, time, cta }) {
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: "#050816", fontFamily: FONT_DISPLAY }}>
      <BackgroundPattern pattern={pattern} palette={palette} />
      <div className="absolute inset-0 flex flex-col items-center" style={{ padding: "8% 6%" }}>
        <WebinarHeader palette={palette} centered showLive={false} />

        {/* Two portraits side by side but smaller than duo layout */}
        <div style={{ marginTop: 60, display: "flex", alignItems: "center", justifyContent: "center", gap: 36 }}>
          <SpeakerPortrait src={PORTRAIT_PASCAL} name="Pascal Schwab" palette={palette} size={220} />
          <SpeakerPortrait src={PORTRAIT_ADRIAN} name="Adrian Bienz" palette={palette} size={220} />
        </div>

        <h1
          style={{
            color: "#fff",
            fontWeight: 900,
            fontSize: 128,
            letterSpacing: "-0.035em",
            lineHeight: 0.9,
            marginTop: 70,
            textAlign: "center",
            background: `linear-gradient(135deg, #fff 60%, ${palette.from})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {title}
        </h1>

        <div style={{ color: "#CBD5E1", fontSize: 30, fontWeight: 500, lineHeight: 1.35, marginTop: 26, maxWidth: "82%", textAlign: "center" }}>
          {subtitle}
        </div>

        <div style={{ marginTop: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 32 }}>
          <WebinarDateTimeCard date={date} time={time} palette={palette} big />
          <div
            style={{
              borderRadius: 999,
              padding: "24px 60px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 900,
              fontSize: 30,
              letterSpacing: "0.22em",
              textAlign: "center",
              boxShadow: `0 20px 70px ${palette.from}66`,
            }}
          >
            {cta} →
          </div>
          <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            alphalane-tradingsystems.com
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Layout 4: "Spotlight" ────────────────────────────────────────────────
// Editorial look: diagonal gradient beam, gradient headline left-aligned,
// staggered overlapping portraits, bullets, full-width date band + CTA.
function WebinarCanvasSpotlight({ palette, pattern, title, subtitle, date, time, cta, b1, b2, b3 }) {
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: "#050816", fontFamily: FONT_DISPLAY }}>
      <BackgroundPattern pattern={pattern} palette={palette} />
      {/* Diagonal light beam (pure gradient — exports cleanly) */}
      <div
        style={{
          position: "absolute",
          top: -360,
          right: -480,
          width: 980,
          height: 1500,
          transform: "rotate(24deg)",
          borderRadius: 260,
          background: `linear-gradient(180deg, ${palette.from}30 0%, ${palette.to}16 45%, transparent 80%)`,
          pointerEvents: "none",
        }}
      />
      <div className="absolute inset-0 flex flex-col" style={{ padding: "6% 6%" }}>
        <WebinarHeader palette={palette} />

        {/* Staggered portraits */}
        <div style={{ marginTop: 70, display: "flex", alignItems: "flex-start", justifyContent: "center", gap: 44 }}>
          <div style={{ marginTop: 70 }}>
            <SpeakerPortrait src={PORTRAIT_PASCAL} name="Pascal Schwab" palette={palette} size={330} />
          </div>
          <SpeakerPortrait src={PORTRAIT_ADRIAN} name="Adrian Bienz" palette={palette} size={330} />
        </div>

        <h1
          style={{
            fontWeight: 900,
            fontSize: 128,
            letterSpacing: "-0.035em",
            lineHeight: 0.94,
            marginTop: 90,
            background: `linear-gradient(120deg, #fff 35%, ${palette.from} 75%, ${palette.to})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {title}
        </h1>

        <div style={{ color: "#CBD5E1", fontSize: 30, fontWeight: 500, lineHeight: 1.4, marginTop: 26, maxWidth: "90%" }}>
          {subtitle}
        </div>

        {[b1, b2, b3].filter(Boolean).length > 0 && (
          <div style={{ marginTop: 48, display: "flex", flexWrap: "wrap", gap: 16 }}>
            {[b1, b2, b3].filter(Boolean).map((b, i) => (
              <div
                key={typeof b === 'string' ? b : i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "16px 28px",
                  borderRadius: 999,
                  border: `1.5px solid ${palette.from}55`,
                  background: `${palette.from}14`,
                }}
              >
                <CheckCircle2 style={{ height: 26, width: 26, color: palette.from, flexShrink: 0 }} />
                <div style={{ color: "#E2E8F0", fontSize: 24, fontWeight: 600 }}>{b}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 30 }}>
          {/* Full-width date band */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 28,
              padding: "30px 38px",
              borderRadius: 28,
              background: `linear-gradient(90deg, ${palette.from}20, ${palette.to}10)`,
              border: `1.5px solid ${palette.from}55`,
            }}
          >
            <div style={{ background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`, borderRadius: 16, padding: 16, display: "flex" }}>
              <Calendar style={{ height: 44, width: 44, color: "#fff" }} />
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 900, fontSize: 42, lineHeight: 1.1, textTransform: "uppercase" }}>
                {date || "Termin folgt"}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                <Clock style={{ height: 26, width: 26, color: palette.from }} />
                <div style={{ color: palette.from, fontWeight: 800, fontSize: 28, fontFeatureSettings: "'tnum'" }}>
                  {(time || "").split(" ")[0]}
                </div>
                <div style={{ color: "#CBD5E1", fontWeight: 600, fontSize: 20, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  {(time || "").split(" ").slice(1).join(" ")}
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              borderRadius: 999,
              padding: "24px 60px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 900,
              fontSize: 30,
              letterSpacing: "0.22em",
              textAlign: "center",
              boxShadow: `0 20px 70px ${palette.from}66`,
            }}
          >
            {cta} →
          </div>
          <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase", textAlign: "center" }}>
            alphalane-tradingsystems.com
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Layout 5: "Ticket" ───────────────────────────────────────────────────
// Event-ticket look: gradient-border ticket card with speakers, dashed
// perforation, huge date + time and a decorative barcode strip.
function WebinarCanvasTicket({ palette, pattern, title, subtitle, date, time, cta, b1, b2, b3 }) {
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: "#050816", fontFamily: FONT_DISPLAY }}>
      <BackgroundPattern pattern={pattern} palette={palette} />
      <div className="absolute inset-0 flex flex-col items-center" style={{ padding: "7% 6%" }}>
        <WebinarHeader palette={palette} showLive={false} centered />

        <h1
          style={{
            color: "#fff",
            fontWeight: 900,
            fontSize: 128,
            letterSpacing: "-0.035em",
            lineHeight: 0.94,
            marginTop: 72,
            textAlign: "center",
          }}
        >
          {title}
        </h1>
        <div style={{ color: "#CBD5E1", fontSize: 29, fontWeight: 500, lineHeight: 1.4, marginTop: 26, maxWidth: "84%", textAlign: "center" }}>
          {subtitle}
        </div>

        {/* Ticket card — gradient border via padding trick */}
        <div
          style={{
            marginTop: 84,
            width: "90%",
            borderRadius: 44,
            padding: 3,
            background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
            boxShadow: `0 30px 90px ${palette.from}33`,
          }}
        >
          <div style={{ borderRadius: 41, background: "#0A0F23", padding: "52px 56px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div
                style={{
                  padding: "10px 24px",
                  borderRadius: 999,
                  border: `1.5px solid ${palette.from}`,
                  color: palette.from,
                  fontWeight: 800,
                  fontSize: 19,
                  letterSpacing: "0.28em",
                  background: `${palette.from}14`,
                }}
              >
                LIVE-EVENT
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src={PORTRAIT_PASCAL} alt="" crossOrigin="anonymous" style={{ height: 100, width: 100, borderRadius: "50%", objectFit: "cover", border: `3px solid ${palette.from}` }} />
                <img src={PORTRAIT_ADRIAN} alt="" crossOrigin="anonymous" style={{ height: 100, width: 100, borderRadius: "50%", objectFit: "cover", border: `3px solid ${palette.to}`, marginLeft: -24 }} />
              </div>
            </div>

            <div style={{ marginTop: 28, color: "#E2E8F0", fontWeight: 700, fontSize: 25, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              Pascal Schwab · Adrian Bienz
            </div>

            {/* Dashed perforation */}
            <div style={{ margin: "42px 0", borderTop: `2.5px dashed ${palette.from}44` }} />

            <div style={{ color: "#94A3B8", fontSize: 17, letterSpacing: "0.26em", fontWeight: 700, textTransform: "uppercase" }}>Datum</div>
            <div style={{ color: "#fff", fontWeight: 900, fontSize: 62, lineHeight: 1.05, marginTop: 12, textTransform: "uppercase" }}>
              {date || "Termin folgt"}
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginTop: 24 }}>
              <div style={{ color: palette.from, fontWeight: 900, fontSize: 54, fontFeatureSettings: "'tnum'" }}>
                {(time || "").split(" ")[0]}
              </div>
              <div style={{ color: "#CBD5E1", fontWeight: 600, fontSize: 27, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                {(time || "").split(" ").slice(1).join(" ")}
              </div>
            </div>

            {/* Decorative barcode strip */}
            <div
              style={{
                marginTop: 44,
                height: 64,
                borderRadius: 10,
                opacity: 0.5,
                background: `repeating-linear-gradient(90deg, ${palette.from} 0px, ${palette.from} 3px, transparent 3px, transparent 9px, ${palette.to} 9px, ${palette.to} 11px, transparent 11px, transparent 20px)`,
              }}
            />
          </div>
        </div>

        {/* Bullets between ticket and CTA */}
        {[b1, b2, b3].filter(Boolean).length > 0 && (
          <div style={{ marginTop: 60, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16, maxWidth: "92%" }}>
            {[b1, b2, b3].filter(Boolean).map((b, i) => (
              <div
                key={typeof b === 'string' ? b : i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 26px",
                  borderRadius: 999,
                  border: `1.5px solid ${palette.from}55`,
                  background: `${palette.from}12`,
                }}
              >
                <CheckCircle2 style={{ height: 24, width: 24, color: palette.from, flexShrink: 0 }} />
                <div style={{ color: "#E2E8F0", fontSize: 23, fontWeight: 600 }}>{b}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 30 }}>
          <div
            style={{
              borderRadius: 999,
              padding: "24px 60px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 900,
              fontSize: 30,
              letterSpacing: "0.22em",
              textAlign: "center",
              boxShadow: `0 20px 70px ${palette.from}66`,
            }}
          >
            {cta} →
          </div>
          <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            alphalane-tradingsystems.com
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Layout 6: "Slogan" ───────────────────────────────────────────────────
// Kein Portrait — großes Zitat/Slogan als Held, Datum-Karte + CTA.
function WebinarCanvasSlogan({ palette, pattern, title, subtitle, slogan, date, time, cta }) {
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: "#050816", fontFamily: FONT_DISPLAY }}>
      <BackgroundPattern pattern={pattern} palette={palette} />
      {/* Decorative gradient orbit ring */}
      <div
        style={{
          position: "absolute",
          top: "26%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 900,
          borderRadius: "50%",
          border: `2px solid ${palette.from}22`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "30%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 640,
          height: 640,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${palette.glow}1c 0%, transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center" style={{ padding: "7% 7%" }}>
        <WebinarHeader palette={palette} showLive={false} centered />

        <div
          style={{
            marginTop: 64,
            padding: "10px 26px",
            borderRadius: 999,
            border: `1.5px solid ${palette.from}`,
            color: palette.from,
            fontWeight: 800,
            fontSize: 20,
            letterSpacing: "0.3em",
            background: `${palette.from}14`,
            textTransform: "uppercase",
          }}
        >
          {title || "Live-Webinar"}
        </div>

        {/* Divider + slogan */}
        <div style={{ marginTop: 90, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div
            style={{
              width: 140,
              height: 8,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${palette.from}, ${palette.to})`,
            }}
          />
          <div
            style={{
              marginTop: 54,
              fontWeight: 900,
              fontSize: 116,
              letterSpacing: "-0.03em",
              lineHeight: 1.04,
              maxWidth: 900,
              background: `linear-gradient(135deg, #fff 40%, ${palette.from} 80%, ${palette.to})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {slogan || "Logik statt Emotion."}
          </div>
        </div>

        <div style={{ color: "#CBD5E1", fontSize: 29, fontWeight: 500, lineHeight: 1.4, marginTop: 48, maxWidth: "82%", textAlign: "center" }}>
          {subtitle}
        </div>

        <div style={{ marginTop: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 32 }}>
          <WebinarDateTimeCard date={date} time={time} palette={palette} big />
          <div
            style={{
              borderRadius: 999,
              padding: "24px 60px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 900,
              fontSize: 30,
              letterSpacing: "0.22em",
              textAlign: "center",
              boxShadow: `0 20px 70px ${palette.from}66`,
            }}
          >
            {cta} →
          </div>
          <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            alphalane-tradingsystems.com
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Layout 7: "Big Stat" ─────────────────────────────────────────────────
// Kein Portrait — eine riesige Kennzahl als Held + Chart + Termin.
function WebinarCanvasStat({ palette, pattern, title, subtitle, date, time, cta,
                             stat1Val, stat1Lbl, stat2Val, stat2Lbl, chartStr, chartLabel }) {
  const series = (chartStr || "")
    .split(/[,;]/)
    .map((x) => parseFloat(x.trim().replace(",", ".")))
    .filter((n) => Number.isFinite(n));
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: "#050816", fontFamily: FONT_DISPLAY }}>
      <BackgroundPattern pattern={pattern} palette={palette} />
      <div className="absolute inset-0 flex flex-col" style={{ padding: "6% 6%" }}>
        <WebinarHeader palette={palette} />

        <h1 style={{ color: "#fff", fontWeight: 900, fontSize: 96, letterSpacing: "-0.035em", lineHeight: 0.94, marginTop: 48 }}>
          {title}
        </h1>
        <div style={{ color: "#CBD5E1", fontSize: 26, fontWeight: 500, lineHeight: 1.4, marginTop: 18, maxWidth: "92%" }}>
          {subtitle}
        </div>

        {/* Giant stat */}
        <div style={{ marginTop: 56 }}>
          <div
            style={{
              fontWeight: 900,
              fontSize: 210,
              letterSpacing: "-0.05em",
              lineHeight: 0.9,
              fontFeatureSettings: "'tnum'",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {stat2Val || "+383%"}
          </div>
          <div style={{ color: "#94A3B8", fontSize: 22, letterSpacing: "0.22em", fontWeight: 700, textTransform: "uppercase", marginTop: 18 }}>
            {stat2Lbl || "Gesamtreturn"}
          </div>
        </div>

        {series.length >= 2 && (
          <div
            style={{
              marginTop: 44,
              borderRadius: 28,
              padding: 26,
              background: "#0B1227aa",
              border: `1.5px solid ${palette.from}33`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <div style={{ color: "#94A3B8", fontSize: 13, letterSpacing: "0.2em", fontWeight: 700, textTransform: "uppercase" }}>
                {chartLabel || "Track Record"}
              </div>
              <div style={{ color: palette.from, fontSize: 16, fontWeight: 800 }}>
                {stat1Val || ""} <span style={{ color: "#64748B", fontWeight: 600, fontSize: 13 }}>{stat1Lbl || ""}</span>
              </div>
            </div>
            <MiniSparkChart series={series} palette={palette} />
          </div>
        )}

        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 28 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 24,
              padding: "26px 34px",
              borderRadius: 26,
              background: `linear-gradient(90deg, ${palette.from}1e, ${palette.to}0e)`,
              border: `1.5px solid ${palette.from}55`,
            }}
          >
            <div style={{ background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`, borderRadius: 14, padding: 14, display: "flex" }}>
              <Calendar style={{ height: 38, width: 38, color: "#fff" }} />
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 900, fontSize: 36, lineHeight: 1.1, textTransform: "uppercase" }}>
                {date || "Termin folgt"}
              </div>
              <div style={{ color: palette.from, fontWeight: 800, fontSize: 24, marginTop: 6 }}>
                {(time || "").split(" ")[0]}{" "}
                <span style={{ color: "#CBD5E1", fontWeight: 600, fontSize: 18, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  {(time || "").split(" ").slice(1).join(" ")}
                </span>
              </div>
            </div>
          </div>
          <div
            style={{
              borderRadius: 999,
              padding: "24px 60px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 900,
              fontSize: 30,
              letterSpacing: "0.22em",
              textAlign: "center",
              boxShadow: `0 20px 70px ${palette.from}66`,
            }}
          >
            {cta} →
          </div>
          <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase", textAlign: "center" }}>
            alphalane-tradingsystems.com
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Layout 8: "Terminal" ─────────────────────────────────────────────────
// Kein Portrait — AI/Code-Terminal-Ästhetik ("Logik statt Emotion").
const FONT_MONO = "'JetBrains Mono', 'SFMono-Regular', Consolas, 'Courier New', monospace";
function WebinarCanvasTerminal({ palette, pattern, title, subtitle, slogan, date, time, cta }) {
  const rows = [
    ["thema", title || "Live-Webinar"],
    ["datum", date || "Termin folgt"],
    ["zeit", time || "—"],
    ["plätze", "limitiert"],
  ];
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ background: "#050816", fontFamily: FONT_DISPLAY }}>
      <BackgroundPattern pattern={pattern} palette={palette} />
      <div className="absolute inset-0 flex flex-col items-center" style={{ padding: "7% 6%" }}>
        <WebinarHeader palette={palette} showLive={false} centered />

        <h1
          style={{
            color: "#fff",
            fontWeight: 900,
            fontSize: 112,
            letterSpacing: "-0.035em",
            lineHeight: 0.94,
            marginTop: 60,
            textAlign: "center",
          }}
        >
          {title}
        </h1>
        <div style={{ color: "#CBD5E1", fontSize: 27, fontWeight: 500, lineHeight: 1.4, marginTop: 22, maxWidth: "84%", textAlign: "center" }}>
          {subtitle}
        </div>

        {/* Terminal window */}
        <div
          style={{
            marginTop: 76,
            width: "90%",
            borderRadius: 26,
            border: `1.5px solid ${palette.from}44`,
            background: "#070B1D",
            boxShadow: `0 30px 90px ${palette.from}26`,
            overflow: "hidden",
          }}
        >
          {/* Title bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "18px 26px",
              borderBottom: `1px solid ${palette.from}26`,
              background: "#0A0F23",
            }}
          >
            <span style={{ height: 16, width: 16, borderRadius: "50%", background: "#F43F5E" }} />
            <span style={{ height: 16, width: 16, borderRadius: "50%", background: "#F59E0B" }} />
            <span style={{ height: 16, width: 16, borderRadius: "50%", background: "#10B981" }} />
            <span style={{ marginLeft: 14, color: "#64748B", fontFamily: FONT_MONO, fontSize: 17, letterSpacing: "0.04em" }}>
              alphalane@ai: ~/webinar
            </span>
          </div>
          {/* Terminal body */}
          <div style={{ padding: "34px 38px", fontFamily: FONT_MONO, fontSize: 26, lineHeight: 2.0, textAlign: "left" }}>
            <div style={{ color: "#64748B" }}>
              <span style={{ color: palette.from }}>$</span> alphalane --next-webinar
            </div>
            {rows.map(([k, v]) => (
              <div key={k} style={{ color: "#E2E8F0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ color: "#64748B" }}>&gt; </span>
                <span style={{ color: palette.from, display: "inline-block", width: 190 }}>{k}</span>
                <span style={{ fontWeight: 700 }}>{v}</span>
              </div>
            ))}
            <div style={{ color: "#E2E8F0" }}>
              <span style={{ color: "#64748B" }}>&gt; </span>
              <span style={{ color: palette.from, display: "inline-block", width: 190 }}>status</span>
              <span style={{ color: "#10B981", fontWeight: 800 }}>● LIVE</span>
            </div>
            <div>
              <span style={{ color: palette.from }}>$</span>{" "}
              <span
                style={{
                  display: "inline-block",
                  width: 24,
                  height: 34,
                  verticalAlign: "middle",
                  background: palette.from,
                  opacity: 0.85,
                }}
              />
            </div>
          </div>
        </div>

        {/* Slogan */}
        <div
          style={{
            marginTop: 60,
            fontWeight: 900,
            fontSize: 52,
            letterSpacing: "-0.02em",
            textAlign: "center",
            background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {slogan || "Logik statt Emotion."}
        </div>

        <div style={{ marginTop: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 28 }}>
          <div
            style={{
              borderRadius: 999,
              padding: "24px 60px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 900,
              fontSize: 30,
              letterSpacing: "0.22em",
              textAlign: "center",
              boxShadow: `0 20px 70px ${palette.from}66`,
            }}
          >
            {cta} →
          </div>
          <div style={{ color: "#64748B", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            alphalane-tradingsystems.com
          </div>
        </div>
      </div>
    </div>
  );
}


// SVG-based mini sparkline for the Webinar performance chart. Matches the
// glow-line style of the big Performance canvas: gradient line (from→to),
// gradient area fill (glow with fade), faint horizontal grid lines and
// solid endpoint dots so the growth story reads at a glance.
function MiniSparkChart({ series: rawSeries, palette }) {
  const series = densifySeries(rawSeries);
  const W = 1000;
  const H = 260;
  const pad = 22;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = Math.max(max - min, 1);
  const toPt = (v, i) => [
    pad + (i / (series.length - 1)) * (W - 2 * pad),
    H - pad - ((v - min) / range) * (H - 2 * pad),
  ];
  const pts = series.map(toPt);
  const linePath = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${pts[pts.length - 1][0]} ${H} L ${pts[0][0]} ${H} Z`;
  const uid = palette.from.replace("#", "");
  const lineGradId = `mini-line-${uid}`;
  const areaGradId = `mini-area-${uid}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <defs>
        <linearGradient id={lineGradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={palette.from} />
          <stop offset="100%" stopColor={palette.to} />
        </linearGradient>
        <linearGradient id={areaGradId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={palette.glow} stopOpacity="0.4" />
          <stop offset="100%" stopColor={palette.glow} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Faint horizontal grid */}
      {[0.25, 0.5, 0.75].map((y) => (
        <line key={y} x1="0" y1={H * y} x2={W} y2={H * y} stroke="#1E293B" strokeWidth="1.2" strokeDasharray="3 9" />
      ))}
      {/* Area fill */}
      <path d={areaPath} fill={`url(#${areaGradId})`} />
      {/* Line */}
      <path d={linePath} fill="none" stroke={`url(#${lineGradId})`} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      {/* Endpoint dots only — line is densified with market noise */}
      {[pts[0], pts[pts.length - 1]].map(([x, y], i) => (
        <circle
          key={i === 0 ? "start" : "end"}
          cx={x}
          cy={y}
          r={8}
          fill={i === 1 ? palette.to : palette.from}
          stroke="#0B1227"
          strokeWidth={2}
        />
      ))}
    </svg>
  );
}

function BigQuoteCanvas({ palette, pattern, text, author }) {
  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{ background: "#050816", fontFamily: FONT_DISPLAY }}
    >
      <BackgroundPattern pattern={pattern} palette={palette} />

      <div className="absolute inset-0 flex flex-col items-center" style={{ padding: "9% 7%" }}>
        {/* Top logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <img src={LOGO_URL} alt="" crossOrigin="anonymous" style={{ height: 90, width: 90 }} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 40, letterSpacing: "0.04em", lineHeight: 1 }}>ALPHALANE AI</div>
            <div style={{ color: "#94A3B8", fontWeight: 600, fontSize: 15, letterSpacing: "0.25em", marginTop: 6 }}>LOGIK STATT EMOTION</div>
          </div>
        </div>

        {/* Big Quote */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", width: "100%" }}>
          <Quote style={{ height: 100, width: 100, color: palette.from, marginBottom: 20 }} />
          <div
            style={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 86,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              textAlign: "center",
              fontStyle: "italic",
              maxWidth: "92%",
              background: `linear-gradient(135deg, #fff 0%, ${palette.from} 50%, ${palette.to} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            „{text}&rdquo;
          </div>
          <div
            style={{
              marginTop: 60,
              height: 4,
              width: 220,
              background: `linear-gradient(90deg, ${palette.from}, ${palette.to})`,
              borderRadius: 999,
            }}
          />
          <div
            style={{
              marginTop: 32,
              color: palette.from,
              fontWeight: 700,
              fontSize: 30,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
            }}
          >
            — {author}
          </div>
        </div>

        {/* Bottom domain */}
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#64748B", fontSize: 18, letterSpacing: "0.18em" }}>alphalane-tradingsystems.com</div>
        </div>
      </div>
    </div>
  );
}

function PromoCanvas({ palette, pattern, headline, sub, b1, b2, b3, cta, url }) {
  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{ background: "#050816", fontFamily: FONT_DISPLAY }}
    >
      <BackgroundPattern pattern={pattern} palette={palette} />

      <div className="absolute inset-0 flex flex-col" style={{ padding: "9% 7%" }}>
        {/* Top logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <img src={LOGO_URL} alt="" crossOrigin="anonymous" style={{ height: 92, width: 92 }} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 42, letterSpacing: "0.04em", lineHeight: 1 }}>ALPHALANE AI</div>
            <div style={{ color: "#94A3B8", fontWeight: 600, fontSize: 16, letterSpacing: "0.25em", marginTop: 8 }}>LOGIK STATT EMOTION</div>
          </div>
        </div>

        {/* Badge */}
        <div
          style={{
            marginTop: 90,
            display: "inline-block",
            alignSelf: "flex-start",
            padding: "10px 22px",
            borderRadius: 999,
            border: `1.5px solid ${palette.from}`,
            color: palette.from,
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "0.25em",
            background: `${palette.from}11`,
          }}
        >
          ★ NEU
        </div>

        {/* Headline */}
        <h1
          style={{
            color: "#fff",
            fontWeight: 900,
            fontSize: 130,
            letterSpacing: "-0.03em",
            lineHeight: 0.95,
            marginTop: 28,
            background: `linear-gradient(135deg, #fff 0%, ${palette.from} 60%, ${palette.to} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {headline}
        </h1>

        {/* Sub */}
        <div
          style={{
            color: "#CBD5E1",
            fontSize: 36,
            fontWeight: 500,
            lineHeight: 1.3,
            marginTop: 36,
            maxWidth: "92%",
          }}
        >
          {sub}
        </div>

        {/* Bullets */}
        <div style={{ marginTop: 56, display: "flex", flexDirection: "column", gap: 22 }}>
          {[b1, b2, b3].filter(Boolean).map((b, i) => (
            <div key={typeof b === 'string' ? b : i} style={{ display: "flex", alignItems: "center", gap: 22 }}>
              <div
                style={{
                  height: 56,
                  width: 56,
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: `0 6px 22px ${palette.from}44`,
                }}
              >
                <CheckCircle2 style={{ height: 30, width: 30, color: "#fff" }} />
              </div>
              <div style={{ color: "#E2E8F0", fontSize: 32, fontWeight: 600 }}>{b}</div>
            </div>
          ))}
        </div>

        {/* CTA + Footer */}
        <div style={{ marginTop: "auto", paddingTop: 32 }}>
          <div
            style={{
              borderRadius: 999,
              padding: "30px 40px",
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 800,
              fontSize: 32,
              letterSpacing: "0.18em",
              textAlign: "center",
              boxShadow: `0 14px 50px ${palette.from}55`,
            }}
          >
            {cta} →
          </div>
          <div style={{ textAlign: "center", marginTop: 28 }}>
            <div style={{ color: "#94A3B8", fontSize: 22, letterSpacing: "0.15em", fontWeight: 500 }}>{url}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TestimonialCanvas({ palette, pattern, text, name, role, rating }) {
  const initials = (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "—";

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{ background: "#050816", fontFamily: FONT_DISPLAY }}
    >
      <BackgroundPattern pattern={pattern} palette={palette} />

      <div className="absolute inset-0 flex flex-col" style={{ padding: "9% 7%" }}>
        {/* Top logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <img src={LOGO_URL} alt="" crossOrigin="anonymous" style={{ height: 90, width: 90 }} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 40, letterSpacing: "0.04em", lineHeight: 1 }}>ALPHALANE AI</div>
            <div style={{ color: "#94A3B8", fontWeight: 600, fontSize: 15, letterSpacing: "0.25em", marginTop: 6 }}>LOGIK STATT EMOTION</div>
          </div>
        </div>

        {/* Title badge */}
        <div
          style={{
            marginTop: 80,
            display: "inline-block",
            alignSelf: "flex-start",
            padding: "10px 22px",
            borderRadius: 999,
            border: `1.5px solid ${palette.from}`,
            color: palette.from,
            fontWeight: 700,
            fontSize: 20,
            letterSpacing: "0.3em",
            background: `${palette.from}11`,
          }}
        >
          KUNDENSTIMME
        </div>

        {/* Quote text */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Quote style={{ height: 80, width: 80, color: palette.from, marginBottom: 30 }} />
          <div
            style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 64,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
              fontStyle: "italic",
            }}
          >
            „{text}&rdquo;
          </div>
        </div>

        {/* Author card */}
        <div
          style={{
            borderRadius: 28,
            border: `1.5px solid ${palette.from}33`,
            background: "#0B1227cc",
            padding: 32,
            display: "flex",
            alignItems: "center",
            gap: 26,
            marginBottom: 36,
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              height: 100,
              width: 100,
              borderRadius: 999,
              background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
              color: "#fff",
              fontWeight: 800,
              fontSize: 42,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: `0 8px 28px ${palette.from}66`,
            }}
          >
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 34 }}>{name}</div>
            <div style={{ color: "#94A3B8", fontWeight: 500, fontSize: 22, marginTop: 4 }}>{role}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  style={{
                    height: 32,
                    width: 32,
                    color: n <= (rating || 0) ? "#FBBF24" : "#334155",
                    fill: n <= (rating || 0) ? "#FBBF24" : "transparent",
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Domain */}
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#64748B", fontSize: 18, letterSpacing: "0.18em" }}>alphalane-tradingsystems.com</div>
        </div>
      </div>
    </div>
  );
}
